/* eslint-disable */

export interface AuditableEntity {
  auditMetaData?: {
    actorId: number;
    actorAccountContextId: number;
    previousEntityState?: any;
  };
}

export type AuditLogActions = 'created' | 'updated' | 'deleted' | 'read';

export type AuditLogChangeTypes = 'CREATE' | 'REMOVE' | 'CHANGE';

export enum TimelineTimes {
  FiveMin = '5min',
  TenMin = '10min',
  ThirtyMin = '30min',
  TwoHour = '2h',
  OneDay = '1d',
}

export interface AuditLogs {
  id: string;
  actorAccountContextId: number;
  actorId: number;
  entityName: string;
  entityId?: string;
  action: AuditLogActions;
}

export interface AuditLogChanges {
  id: string;
  auditLogId: string;
  path: (string | number)[];
  type: AuditLogChangeTypes;
  value?: any;
  oldValue?: any;
}

export enum DateFormat {
  LONG = 'MMM d, yyyy',
  LONG_FULL_MONTH = 'MMMM d, yyyy',

  LONGTIME = 'MMM d, yyyy h:mm a',
  LONGTIME_DOW = `EEE, MMM d, yyyy 'at' h:mm a`,
  H_M = 'HH:mm',
  TIME = 'h:mm a',
  YMD = 'yyyy-MM-dd',
  FILE_FORMAT = 'yyyy_MM_dd_hh_mm_a',
  M_D = 'MMM d',
  D = 'd',
  DATE_NTH = 'do',
  Y = 'yyyy',
  M_DATE_NTH = 'MMMM do',
}

export enum Intents {
  // Automation Intents
  GivingIntent = 'GivingIntent',
  SocialIntent = 'SocialIntent',
  SermonIntent = 'SermonIntent',
  WorshipMediaIntent = 'WorshipMediaIntent',
  ServiceLocationsIntent = 'ServiceLocationsIntent',
  ServiceTimesIntent = 'ServiceTimesIntent',
  GeneralInfoIntent = 'GeneralInfoIntent',
  WebsiteIntent = 'WebsiteIntent',
  DoctrineIntent = 'DoctrineIntent',
  // Default Intents
  GreetingIntent = 'GreetingIntent',
  GratitudeIntent = 'GratitudeIntent',
  NoResponseIntent = 'NoResponseIntent',
  NotFoundIntent = 'NotFoundIntent',
  UnsubscribeIntent = 'UnsubscribeIntent',
  ResubscribeIntent = 'ResubscribeIntent',
  SpamReportIntent = 'SpamReportIntent',
  // Change Intents
  ChangeNameIntent = 'ChangeNameIntent',
  ChangeEmailIntent = 'ChangeEmailIntent',
  EnterEmailIntent = 'EnterEmailIntent',
  EnterPrayerRequestIntent = 'EnterPrayerRequestIntent',
  EnterStoryIntent = 'EnterStoryIntent',
}

export enum Permissions {
  ADMIN, // this defaults to number enums, so ADMIN = 0, PRAYER_VIEW = 1, 🪄
  PRAYER_VIEW,
  STORIES_VIEW,
  PRAYER_EDIT,
  BROADCASTS_EDIT,
  DEVOTIONALS_EDIT,
  ACTIONS_EDIT,
  PEOPLE_VIEW,
  PEOPLE_EDIT,
  DATA_VIEW,
  EXPLORER_CONNECTIONS_EDIT,
  GLOO_PLUS_PURCHASER,
}

export type SubscriptionInfo = {
  stripeSubscriptionId?: string;
  legacyStripeSubscriptionId?: string;
};

export enum SupportedLanguages {
  en = 'en',
  es = 'es',
  fr = 'fr',
}

export type UserMessageI18n = {
  default: SupportedLanguages;
  [SupportedLanguages.en]?: string;
  [SupportedLanguages.es]?: string;
  [SupportedLanguages.fr]?: string;
};

export type MessageMedia = {
  url: string;
  cloudinaryId: string;
  resourceType: 'video' | 'image';
  version?: string;
  shortUrl?: string; // used for videos in messaging
};

export interface UserMessage extends UserMessageI18n {
  media?: MessageMedia[];
}

export type OrgStatus = 'active' | 'inactive' | 'canceled' | 'suspended' | 'expired' | 'unverified';

export type FeatureAccessStrings = 'text' | 'video' | 'unlimited' | 'limited';

export type FeatureConfiguration = {
  enabled: boolean;
  access: FeatureAccessStrings | string | null;
  allowOverLimitNumber?: number;
  perLicense?: boolean;
  multiplier?: { perPeople?: number; value?: number };
};

export enum OrgBrand {
  RCI = 'RCI',
  RSCI = 'RSCI',
  R1 = 'R1',
  SBC = 'SBC',
}

export enum OrgType {
  RECOVERY = 'RECOVERY',
  CHURCH = 'CHURCH',
}

export enum OrganizationTypes {
  CHURCH = 'CHURCH',
  NETWORK = 'NETWORK',
  INDIVIDUAL = 'INDIVIDUAL',
  SUBACCOUNT = 'SUBACCOUNT',
}

export type OrgMessageVolume =
  | '10'
  | '100'
  | '1,000'
  | '10,000'
  | '100,000'
  | '250,000'
  | '500,000'
  | '750,000'
  | '1,000,000'
  | '5,000,000'
  | '10,000,000+';

export type OrgUseCaseCategory =
  | '2FA'
  | 'App Notifications'
  | 'Appointments'
  | 'Auctions'
  | 'Auto Repair Services'
  | 'Bank Transfers'
  | 'Billing'
  | 'Booking Confirmations'
  | 'Business Updates'
  | 'Career Training'
  | 'Chatbot'
  | 'Conversational / Alerts'
  | 'Courier Services & Deliveries'
  | 'COVID-19 Alerts'
  | 'Emergency Alerts'
  | 'Events & Planning'
  | 'Financial Services'
  | 'Fraud Alerts'
  | 'Fundraising'
  | 'General Marketing'
  | 'General School Updates'
  | 'Healthcare Alerts'
  | 'Housing Community Updates'
  | 'HR / Staffing'
  | 'Insurance Services'
  | 'Job Dispatch'
  | 'Legal Services'
  | 'Mixed'
  | 'Motivational Reminders'
  | 'Notary Notifications'
  | 'Order Notifications'
  | 'Political'
  | 'Public Works'
  | 'Real Estate Services'
  | 'Religious Services'
  | 'Repair and Diagnostics Alerts'
  | 'Rewards Program'
  | 'Surveys'
  | 'System Alerts'
  | 'Voting Reminders'
  | 'Waitlist Alerts'
  | 'Webinar Reminders'
  | 'Workshop Alerts';

export type ServiceTime = {
  start: Date;
  end: Date;
};

export type Services = Partial<Record<DayOfTheWeek, ServiceTime[]>>;

export enum DayOfTheWeek {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}

export type OrgConfig = {
  info?: {
    helpEmail?: string;
    contactNumber?: string;
    contactEmail?: string;
    contactName?: string;
    address?: string;
    addressLine2?: string | null;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    timeZone?: string;
    type?: OrganizationTypes;
    invoiceEmail?: string;
    averageWeeklyAttendance?: number;
    numberOfChurches?: number;
    organizationFirstConnectionDate?: Date;
    organizationResponseToFirstConnectionDate?: Date;
    orgFirstAccessedDate?: Date;
    orgWebsiteUrl?: string;
    serviceLocation?: {
      address?: string;
      addressLine2?: string | null;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    organizationEIN?: string | null;
    services?: Services;
  };
  platforms?: PlatformType[];
  programsAndServices?: OrgProgramsAndServices;
  features?: {
    prayerRequests?: FeatureConfiguration;
    people?: FeatureConfiguration;
    calendar?: FeatureConfiguration;
    stories?: FeatureConfiguration;
    devotionals?: FeatureConfiguration;
    surveys?: FeatureConfiguration;
    broadcasts?: FeatureConfiguration;
    automations?: FeatureConfiguration;
    customActions?: FeatureConfiguration;
    insights?: FeatureConfiguration;
    metricsCapture?: FeatureConfiguration;
  };
  interactions?: InteractionsObject;
  onboardingMessage?: UserMessage;
  customizedResponses?: OrgCustomizedResponses;
  disabledResponses?: OrgDisabledResponses;
  collectEmail?: { enabled?: boolean; attempts?: number };
  preferences?: any;
  apiKey?: string;
  provider?: 'twilio' | 'glooassessmentstwilio' | 'bandwidth' | 'infobip';
  smsNumber?: string;
  smsNumberManualVerificationStatus?: string | null;
  smsSharedNumber?: string;
  messageVolume?: OrgMessageVolume;
  useCaseCategory?: OrgUseCaseCategory;
  whatsAppNumber?: string;
  uniqId?: string;
  type?: OrgType;
  brand?: OrgBrand;
  creditRolloverLimitMultiplier?: number;
  stripeCustomerId?: string;
  legacyStripeCustomerId?: string;
  onboardingFlowId?: string;
  languageDefault?: 'es' | 'en';
  glooNextOrganizationId?: string;
  assessmentConfig?: {
    locationId?: string;
    deployments?: {
      [key in AssessmentType]?: {
        enabled: boolean;
        assessmentId?: string;
        deploymentId?: string;
      };
    };
  };
  maxSubaccounts?: number;
};

export type OrgCustomizedResponses = {
  onboardingMessage?: UserMessage;
  onboardingWithResponseMessage?: UserMessage;
  prayerReceived?: UserMessage;
  prayerPrayedFor?: UserMessage;
  unresolvedMessage?: UserMessage;
  onboardingComplete?: UserMessage;
  prayerFixedResponse?: UserMessage;
  onboardingEmail?: UserMessage;
};

export type OrgDisabledResponses = {
  unresolvedMessage?: boolean;
  onboardingComplete?: boolean;
  prayerReceived?: boolean;
  prayerPrayedFor?: boolean;
};

export type OrgCustomizedResponsesUpdateObj = {
  [key in keyof OrgCustomizedResponses]: string | UserMessage;
};

export type PlanInfo = {
  price?: { label: string; cents: number };
  isFree?: boolean;
  freeTrial?: '7d' | '14d' | '1m' | '30d' | '2m' | '3m';
  stripePriceId?: string;
  stripeProductId?: string;
  starterProductTitle?: string;
  quantityOptions?: number[];
  collectPaymentMethod?: boolean;
  collectPaymentDescription?: string;
  bulletPoints?: string[];
};

export type InteractionsObject = { [key in Interactions]?: boolean };

export type OrgUserAccess = {
  [key in number]: {
    permissions?: Permissions[];
    preferences?: OrgUserPreferences;
    interactions?: InteractionsObject;
  };
};

export type OrgUserPreferences = {
  prayerEmail?: boolean;
  unresolvedMessageEmail?: boolean;
  explorerConnectedEmail?: boolean;
  prayerSms?: boolean;
  unresolvedMessageSms?: boolean;
  explorerConnectedSms?: boolean;
  prayerPush?: boolean;
  unresolvedMessagePush?: boolean;
  explorerConnectedPush?: boolean;
};

export enum Interactions {
  hasTriedSimulator = 'hasTriedSimulator',
  hasSeenOnboarding = 'hasSeenOnboarding',
  hasSeenEngageOnboarding = 'hasSeenEngageOnboarding',
  hasCreatedKeyword = 'hasCreatedKeyword',
  hasChosenPlan = 'hasChosenPlan',
  hasCreatedAutomations = 'hasCreatedAutomations',
}

export enum SuperPermissions {
  GLOO_CONNECT_ROUTER = 'GLOO_CONNECT_ROUTER',
  TOTAL_ACCESS = 'TOTAL_ACCESS',
  DATA_INTELLIGENCE = 'DATA_INTELLIGENCE',
}

export type OrgAccessLimiter = {
  orgTypes?: OrgType[];
  orgBrands?: OrgBrand[];
  orgIds?: number[] | 'ALL';
  permissions?: (Permissions | SuperPermissions)[];
};
export type OrgUserExtra = {
  forgotPasswordToken?: string;
  setupToken?: string;
  isThryveAdmin?: boolean;
  interactions?: InteractionsObject;
  orgAccessLimiters?: OrgAccessLimiter[];
  glooNextUserId?: string;
  glooNextMigrationComplete?: boolean;
  intercomContactId?: string;
  intercomLastSendDate?: number; // Timestamp from Date.now()
  bookmarksImported?: {
    [key in number]: boolean;
  };
};

export type UserInfo = {
  smsNumber?: string;
  allPlatforms?: PlatformType[];
  uniqId?: string;
  email?: string;
  address?: any;
  profile?: any;
  context?: UserContext[];
  onboardingStatus?: 'seen' | 'complete' | 'synced' | undefined;
  photo?: string;
  mailchimpId?: string;
  salesforceId?: string;
  planningCenterId?: string;
  ccbId?: string;
  rockRmsId?: string;
  hubspotId?: string;
  ministryPlatformId?: string;
  hasUnresolved?: boolean;
  inOrgConversationFlow?: boolean;
  matchRequestId?: string;
  smsNumberHistory?: { phone: string; removedAt?: string }[];
  recentIncoming?: {
    message?: string;
    media?: MessageMedia;
    date: Date;
    assignedToOrgUserId?: number;
    assignedToOrgUserName?: string;
    messageType?: MessageType;
  };
};

export type MessageType = 'general' | 'prayerrequest' | 'explorerconnection';

export type ReferenceListInfo = {
  status?: null | 'active' | 'inactive';
  description?: string;
  private?: boolean;
  mailchimpListId?: string;
  planningCenterListId?: string;
  ccbGroupId?: string;
  salesforceGroupId?: string;
  rockRmsGroupId?: string;
  hubspotGroupId?: string;
  ministryPlatformGroupId?: string;
  lastSyncedDate?: string | null;
  isExplorerGroup?: boolean;

  // Fields used by the new integrations implementation. Once switched, we can delete all the specific ids by type above (and references to them).
  integrationGroupId?: string;
  integrationType?: IntegrationsType;

  // Fields used by convo prompt response tips (inline messages above the text box on a response)
  text?: string; // the text of the tip
  title?: string; // something like "Listening Ear." -- this is only shown on the detail of a response tip
  headerCaption?: string; // something like "response tip" or "did you know?" -- only shown on the "inbox" view of the tip
  icon?: string; // optional icon name
  order?: number; // used to sort the results when querying reference lists
  categoryIds?: number[]; // an array of ids that can be used to store a reference to a category, topic, whatever
  showAboveMessages?: boolean; // displays this response tip above the text field in the inbox
  showInConvoDrawer?: boolean; // displays this response tip in the expanded convo prompt flyout drawer

  // Fields used by convo prompts to display an emoji for each "topic"
  emoji?: string;
};

// Contexts are in an ordered array with the most recent at index 0. Whenever we are adding a context,
// first filter out contexts in the array that are of the same type that we are adding. You can only
// have one context of each type. When a context is completed, remove it from the array. If a context
// cannot be completed programatically, time it out after X hours.
export enum UserContextTypes {
  name = 'name',
  address = 'address',
  email = 'email',
  interests = 'interests',
  groups = 'groups',
  rsvp = 'rsvp',
  prayer = 'prayer',
  options = 'options',
  changeName = 'changeName',
  changeEmail = 'changeEmail',
  contextConfig = 'contextConfig',
  workflowMenu = 'workflowMenu',
  // TODO: Add these when reworking the brain
  // onboardingName = 'onboardingName',
  // onboardingCampus = 'onboardingCampus',
}

export type UserContext = {
  type: UserContextTypes;
  createdAt?: Date;
  attempts?: number;
  data?: OptionsContext;
};

export type OptionsContext = {
  contextConfigId?: number;
  workflowStepId?: string;
  questionIndex?: number;
  captureForGroupIds?: number[];
};

export type DevoMessage = {
  id?: string;
  broadcastId?: number;
  dateTime?: Date;
  dayNum?: number;
  message: UserMessageI18n;
  media?: MessageMedia[];
  time?: string;
};

export type DevotionalContent = {
  duration?: string;
  // Everything is based off the index of this array
  messages?: DevoMessage[];
  subscribeMessage?: UserMessageI18n;
  subscribeMedia?: MessageMedia[];
};

export type LookupType = 'twilioSubaccountSid' | 'gjpOrganizationId';

export type PlatformType = 'sms' | 'web';

export type UserConversationStatus = 'NEEDS_RESPONSE' | 'NEEDS_PRAYER' | 'SERVED' | 'DISCONNECTED';

export type PrayerRequestStatus = 'new' | 'complete' | 'assigned';

export type ActivityType =
  | 'devotional'
  | 'customaction'
  | 'automation'
  | 'devotionalcancel'
  | 'devotionaldisabled'
  | 'customactiondisabled'
  | 'storyask'
  | 'prayerrequest'
  | 'prayerrequestask'
  | 'iosreaction'
  | 'androidreaction'
  | 'userunsubscribe'
  | 'userresubscribe'
  | 'userspamreport'
  | 'automationdisabled'
  | 'workflowkeyword';

export type BroadcastInfo = {
  messageCredits?: number;
  estimatedUserCount?: number;
  totalSentCount?: number;
  totalFailedCount?: number;
  estimatedCreditsCount?: number;
  totalCreditsCount?: number;
};

export type ActivityInfo = {
  actionId?: number;
  devotionalId?: number;
  prayerRequestId?: number;
  intent?: Intents;
  keyword?: string;
  workflowId?: string;
  // TODO: Lots more types
};
export type BroadcastStatus =
  | 'pending'
  | 'scheduled'
  | 'sent'
  | 'sending'
  | 'needsattention'
  | 'pending-admin-approval'
  | 'admin-approved'
  | 'draft'
  | 'recurringDraft'
  | 'recurringActive'
  | 'recurringComplete'
  | 'spam';

export type BroadcastType =
  | 'announcement'
  | 'contextconfig'
  | 'contextconfigreminder'
  | 'devotional'
  | 'emailannouncement';

export type ActionStatus = 'active' | 'inactive' | 'deleted';

export type ActionType = 'custom' | 'automated';

export type DefaultStatus = 'active' | 'inactive';

export type DevotionalStatus = 'active' | 'inactive';

export type DevoSubscriberStatus = 'active' | 'inactive' | 'complete' | 'unsubscribed' | 'devoinactive' | 'removed';

export type UserStatus = 'active' | 'inactive' | 'inactive_sync' | 'unsubscribed' | 'blocked' | 'archived' | 'deleted';

export type NotFoundResponseOther = {
  orgIds: number[];
  userIds: number[];
};
export type IntegrationsType =
  | 'salesforce'
  | 'planningcenter'
  | 'planning-center'
  | 'ccb'
  | 'church-community-builder'
  | 'rockrms'
  | 'mailchimp'
  | 'hubspot'
  | 'fellowshipone'
  | 'ministryplatform';

export type IntegrationsAccountUser = {
  id?: string;
  name?: string;
  photo?: string;
  username?: string;
  password?: string;
  hubDomain?: string;
  hubId?: string;
  appId?: string;
};

export type IntegrationsInfo = {
  accessTokenJson?: string;
  ccbChurch?: string;
  mailchimpServerPrefix?: string;
  rockRmsUrl?: string;
  apiKey?: string;
  webhookKey?: string;
};

export enum IntegrationsSyncStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
}

export type IntegrationsSync = {
  listIds?: string[];
  createIfNew?: boolean;
  syncAllPeople?: boolean;
  lastSyncedAllPeopleDate?: string | null;
  lastSyncedCreateIfNew?: string | null;
  lastSyncedIntegration?: string | null;
  groupSync?: GroupSync[];
  manualSyncStatus?: IntegrationsSyncStatus;
  manualSyncStatusTimestamp?: string;
};

export type GroupSync = {
  mailchimpListId: string;
  syncEnabled: boolean;
  thryveGroupId: number;
};

export type IntegrationGroupObj = {
  id: string;
  name: string;
  total: number;
  category?: string;
  campus?: string;
  description?: string;
  createdAt?: string;
};

export type DynamoMessageLogType =
  | 'singleuser'
  | 'broadcast'
  | 'contextconfig'
  | 'contextconfigreminder'
  | 'synconboarding'
  | 'devomessage'
  | 'prayerresponse'
  | 'unresolved'
  | 'prayerinput';

export type DynamoMessageResponseType =
  | 'Action'
  | 'ConfirmNonStandardName'
  | 'DevotionalAlreadySubscribed'
  | 'DevotionalFirstTimeSubscribe'
  | 'PrayerRequestSimple'
  | 'NotFound'
  | 'NoResponseClearContext'
  | 'AnswerName'
  | 'AskNameAgain'
  | 'AskEmailAgain'
  | 'AskNameAgainTooLong'
  | 'AnswerChangeName'
  | 'AnswerChangeEmail'
  | 'EnterEmailAddress'
  | 'AnswerEmailAddress'
  | 'EnterPrayerRequest'
  | 'EnterStory'
  | 'AnswerPrayerRequest'
  | 'UserUnsubscribe'
  | 'UserResubscribe'
  | 'AnswerContextConfig'
  | 'ContextConfigResponse'
  | 'ContextConfigResponseAskAgain'
  | 'ContextConfigResponseSkip'
  | 'WorkflowMenuResponse'
  | 'WorkflowMenuResponseAskAgain'
  | 'WorkflowMenuResponseSkip'
  | 'TwilioHelpOrInfo'
  | 'ChangeNameIntent'
  | 'ChangeEmailIntent'
  | 'EnterPrayerRequestIntent'
  | 'EnterStoryIntent'
  | 'EnterEmailIntent'
  | 'GreetingIntent'
  | 'GratitudeIntent'
  | 'NoResponseIntent'
  | 'NotFoundIntent'
  | 'UserSpamReport'
  | 'ResubscribeIntent'
  | 'UnsubscribeIntent';

export type DynamoMessageIntentType =
  | 'GreetingIntent'
  | 'EnterEmailIntent'
  | 'GratitudeIntent'
  | 'NoResponseIntent'
  | 'NotFoundIntent'
  | 'ChangeNameIntent'
  | 'ChangeEmailIntent'
  | 'GivingIntent'
  | 'DoctrineIntent'
  | 'GeneralInfoIntent'
  | 'SermonIntent'
  | 'ServiceLocationsIntent'
  | 'ServiceTimesIntent'
  | 'SocialIntent'
  | 'WebsiteIntent'
  | 'WorshipMediaIntent';

export type DynamoMessageMatchMethod =
  | 'StandardKeywordMatch'
  | 'WorkflowKeywordMatch'
  | 'LexNlpMatch'
  | 'ContextMatch'
  | 'CustomKeywordMatch'
  | 'SmsReactionMatch'
  | 'SimplePrayerRequestMatch'
  | 'SimpleStoryMatch';

export type DynamoMessageStatusCallback = {
  status: 'delivered' | 'failed' | string;
  code: string;
  description: string;
  provider: 'bandwidth' | string;
  messageId: string;
  sentAt: string;
  statusAt: string;
};

export type DynamoResponseMessage = {
  assignedToOrgUser?: OrgUsers;
  assignment?: OrgUsersToMessageAssignments;
  userId: string;
  timestamp: string;
  processingTime?: string;
  message?: string;
  media?: MessageMedia[];
  cleanedMessage?: string;
  orgId?: string;
  direction?: string;
  phone?: string;
  provider?: string;
  messageId?: string;
  type?: DynamoMessageLogType;
  responseType?: DynamoMessageResponseType;
  intentType?: DynamoMessageIntentType;
  matchMethod?: DynamoMessageMatchMethod;
  orgUserId?: string;
  orgUser?: OrgUsers;
  broadcastId?: string;
  devotionalId?: string;
  prayerRequestId?: string;
  hidden?: boolean;
  messageCharCount?: number;
  mediaCount?: number;
  messageType?: MessageType;
  isResolved?: boolean;
  statusCallback?: DynamoMessageStatusCallback;
};

export type TimeFrame =
  | '1w'
  | '2w'
  | '3w'
  | '4w'
  | '1m'
  | '2m'
  | '3m'
  | '6m'
  | '1y'
  | '10y'
  | '1d'
  | '2d'
  | '3d'
  | '4d'
  | '5d'
  | '6d'
  | '30d'
  | '60d'
  | '90d';

export enum ContextConfigTypes {
  metricsets = 'metricsets',
}

export type ContextConfigTypesString = 'metricsets';

export type ContextConfigStatus = 'unpublished' | 'active' | 'completed';

export type RepeatByDayOptions = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

export type ContextConfigRepeat = {
  // timezone: Look up from org
  // Reference for calendar recurrence: https://icalendar.org/iCalendar-RFC-5545/3-8-5-3-recurrence-rule.html
  time?: string; // "09:00" | "13:00"
  enabled: boolean;
  freqeuncy?: string;
  byDay?: RepeatByDayOptions[];
  interval?: number;
  scheduledDate?: Date | string;
  endDate?: Date | string;
  reminderAfterDays?: 1 | 2 | 3;
};

export type Question = {
  text: UserMessageI18n | string;
  metricId: number; // MetricId from down below
  captureForUsersGroups?: boolean;
};

export type ContextConfigContent = {
  uniqId: string;
  keywords?: string[];
  questions?: Question[];
  endingResponse: UserMessageI18n | string;
  endingResponseMedia?: MessageMedia[];
  metrics: {
    id: number;
    type: 'numeric' | 'subjective';
    label: string;
  }[];
  type?: ContextConfigContentType;
};

export enum ContextConfigContentType {
  SURVEY_OPT_IN = 'SURVEY_OPT_IN',
  SURVEY_SCHEDULED = 'SURVEY_SCHEDULED',
}

export type UserDataCaptureInfo = { metricId?: number; captureForGroupId?: number };

export type EffectInstructions = {
  addToGroupId?: number;
  addToGroup?: ReferenceLists;
  removeFromGroupId?: number;
  removeFromGroup?: ReferenceLists;
  emailOrgUserIds?: number[];
  emailOrgUsers?: OrgUsers[];
  removeFromDevotionalId?: number;
  removeFromDevotional?: Devotionals;
};

export type TransactionInfo = {};

export type MsgTotalsInfo = {
  startDate?: Date;
  rolloverCredits: number;
  currentCredits: number;
};

/**
 *
 *
 *
 *
 * DB Models
 * Needs to be updated whenever the Models change
 *
 *
 *
 *
 */
export interface Actions {
  id: number;
  name: string;
  status: ActionStatus;
  response: UserMessageI18n;
  type?: ActionType;
  keywordIds: number[];
  keywords: Keywords[];
  media?: MessageMedia[];
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  actionEffects?: Effects[];
}

export interface Activities {
  id: number;
  type: ActivityType;
  info: ActivityInfo;
  orgId: number;
  userId?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  user?: Users;
}

export interface Broadcasts {
  id: number;
  status: BroadcastStatus;
  message: UserMessageI18n;
  media?: MessageMedia[];
  info: BroadcastInfo;
  time: Date | string;
  groups: number[];
  users: number[];
  primaryContactOrgIds: number[];
  excludedGroups?: number[];
  excludedUsers?: number[];
  orgId: number;
  type: BroadcastType;
  contextConfigId?: number;
  creatorOrgUserId?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  contextConfig?: ContextConfigs;
  groupsArr?: ReferenceLists[];
  usersArr?: Users[];
  primaryContactOrgsArr?: Pick<Organizations, 'id' | 'config'>[];
  excludedGroupsArr?: ReferenceLists[];
  excludedUsersArr?: Users[];
  creatorOrgUser?: OrgUsers;
  devotionalId?: number;
  messageFromName?: string;
  messageReplyToEmailAddress?: string;
  messageSubject?: string;
  richTextMessage?: string;
  attachedAssessmentId?: string;
  nextSendDate?: Date;
  broadcastIntervalId?: string;
  parentBroadcastId?: number;
  broadcastInterval?: BroadcastInterval | null;
  parentBroadcast?: Broadcasts;
  childBroadcasts?: Broadcasts[];
  devotional?: Devotionals;
}

export enum BroadcastIntervalType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum BroadcastMonthlyRepeatOrdinal {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
  THIRD = 'THIRD',
  FOURTH = 'FOURTH',
  FIFTH = 'FIFTH',
  LAST = 'LAST',
}

export enum BroadcastMonthlyRepeatTime {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
  DAY = 'DAY',
  WEEKDAY = 'WEEKDAY',
}

export interface BroadcastInterval {
  id?: string;
  endDate?: Date | null;
  type: BroadcastIntervalType;
  weeklyConfig?: BroadcastWeeklyConfig | null;
  monthlyConfig?: BroadcastMonthlyConfig | null;
}

export interface BroadcastWeeklyConfig {
  id?: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export interface BroadcastMonthlyConfig {
  id?: string;
  day?: number | null;
  repeats?: BroadcastMonthlyRepeatOrdinal | null;
  repeatTime?: BroadcastMonthlyRepeatTime | null;
}

export interface Devotionals {
  id: number;
  name: string;
  image: string;
  description?: string;
  orgId: number;
  status: DevotionalStatus;
  keywordIds: number[];
  keywords: Keywords[];
  content: DevotionalContent;
  type?: DevotionalType;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  subscribers?: DevoSubscribers[];
  devotionalEffects?: Effects[];
}

export interface Logger {
  id: number;
  log: object;
  createdAt: Date;
  updatedAt: Date;
}

export interface Lookup {
  id: number;
  type: LookupType;
  value: string;
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
}

export interface NotFoundResponses {
  id: number;
  text: string;
  other: NotFoundResponseOther;
  times: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
}

export enum Traditions {
  'Adventist' = 'Adventist',
  'Anabaptist' = 'Anabaptist',
  'Anglican/Episcopalian' = 'Anglican/Episcopalian',
  'Baptist' = 'Baptist',
  'Catholic' = 'Catholic',
  'Congregational' = 'Congregational',
  'Friends' = 'Friends',
  'Holiness Movement' = 'Holiness Movement',
  'Lutheran' = 'Lutheran',
  'Methodist' = 'Methodist',
  'Nondenominational' = 'Nondenominational',
  'Pentecostal' = 'Pentecostal',
  'Pietist' = 'Pietist',
  'Presbyterian' = 'Presbyterian',
  'Reformed' = 'Reformed',
  'Restorationist' = 'Restorationist',
  'Other' = 'Other',
}

export type Tradition = keyof typeof Traditions;
export const traditionsList = Object.keys(Traditions).sort() as Tradition[];

export enum SubDenominations {
  'Advent Christian' = 'Advent Christian',
  'African Methodist Episcopal' = 'African Methodist Episcopal',
  'African Methodist Episcopal Zion' = 'African Methodist Episcopal Zion',
  'American Baptist Churches USA' = 'American Baptist Churches USA',
  'American Lutheran Church/Lutheran Church in America' = 'American Lutheran Church/Lutheran Church in America',
  'Amish' = 'Amish',
  'Anglican Church (Church of England)' = 'Anglican Church (Church of England)',
  'Anglican Orthodox Church' = 'Anglican Orthodox Church',
  'Episcopal Church' = 'Episcopal Church',
  'Apostolic Holiness Church' = 'Apostolic Holiness Church',
  'Apostolic Lutheran Church in America' = 'Apostolic Lutheran Church in America',
  'Apostolic Pentecostal' = 'Apostolic Pentecostal',
  'Assemblies of God' = 'Assemblies of God',
  'Assembly of Christian Churches' = 'Assembly of Christian Churches',
  'Associate Reformed Presbyterian' = 'Associate Reformed Presbyterian',
  'Association of Bridge Churches' = 'Association of Bridge Churches',
  'Baptist General Convention of Texas' = 'Baptist General Convention of Texas',
  'Baptist Bible Church, including Baptist Bible Fellowship' = 'Baptist Bible Church, including Baptist Bible Fellowship',
  'Baptist Bible Fellowship' = 'Baptist Bible Fellowship',
  'Baptist Missionary Association' = 'Baptist Missionary Association',
  'Bible, Gospel, Missionary Churches' = 'Bible, Gospel, Missionary Churches',
  'Bible Presbyterian Church' = 'Bible Presbyterian Church',
  'Born again' = 'Born again',
  'Brethren in Christ' = 'Brethren in Christ',
  'Brethren in Christ, not further specified' = 'Brethren in Christ, not further specified',
  'Calvinist' = 'Calvinist',
  'Calvary Chapel' = 'Calvary Chapel',
  'Charismatic' = 'Charismatic',
  'Charismatic Presbyterian' = 'Charismatic Presbyterian',
  'Christian and Missionary Alliance' = 'Christian and Missionary Alliance',
  'Christian Churches and Churches of Christ' = 'Christian Churches and Churches of Christ',
  'Christian Methodist Episcopal Church' = 'Christian Methodist Episcopal Church',
  'Christian Reformed Church' = 'Christian Reformed Church',
  'Church of Christ' = 'Church of Christ',
  'Church of God (Anderson, Ind.)' = 'Church of God (Anderson, Ind.)',
  'Church of God (Cleveland, Tenn.)' = 'Church of God (Cleveland, Tenn.)',
  'Church of God in Christ' = 'Church of God in Christ',
  'Church of God of Kentucky' = 'Church of God of Kentucky',
  'Church of God of Prophecy' = 'Church of God of Prophecy',
  'Church of God of the Apostolic Faith' = 'Church of God of the Apostolic Faith',
  'Church of the Living God' = 'Church of the Living God',
  'Church of the Brethren' = 'Church of the Brethren',
  'Church of the Lutheran Confession' = 'Church of the Lutheran Confession',
  'Church of the Nazarene' = 'Church of the Nazarene',
  'Church of God General Conference' = 'Church of God General Conference',
  'Church of God (Seventh-day)' = 'Church of God (Seventh-day)',
  'Church of Ireland' = 'Church of Ireland',
  'Churches of Christ in Christian Union' = 'Churches of Christ in Christian Union',
  'Community church' = 'Community church',
  'Congregational Methodist' = 'Congregational Methodist',
  'Congregational Presbyterian' = 'Congregational Presbyterian',
  'Conservative Baptist Association of America' = 'Conservative Baptist Association of America',
  'Conservative Congregational Christian' = 'Conservative Congregational Christian',
  'Conservative Lutheran' = 'Conservative Lutheran',
  'Conservative Presbyterian Church' = 'Conservative Presbyterian Church',
  'Converge' = 'Converge',
  'Cooperative Baptist Fellowship; Baptist Alliance' = 'Cooperative Baptist Fellowship; Baptist Alliance',
  'Cowboy Church Baptist' = 'Cowboy Church Baptist',
  'Covenant Presbyterian Church' = 'Covenant Presbyterian Church',
  'Cumberland Presbyterian Church' = 'Cumberland Presbyterian Church',
  'Deeper Life Bible Church/Deeper Christian Life Ministry' = 'Deeper Life Bible Church/Deeper Christian Life Ministry',
  'Disciples of Christ' = 'Disciples of Christ',
  'Ecumenical' = 'Ecumenical',
  'ECO: A Covenant Order of Evangelical Presbyterians' = 'ECO: A Covenant Order of Evangelical Presbyterians',
  'Electronic ministries' = 'Electronic ministries',
  'Elim Fellowship' = 'Elim Fellowship',
  'Emergent Church' = 'Emergent Church',
  'Ethnic Baptist' = 'Ethnic Baptist',
  'Evangelical' = 'Evangelical',
  'Evangelical Anglican/Episcopalian' = 'Evangelical Anglican/Episcopalian',
  'Evangelical Baptist' = 'Evangelical Baptist',
  'Evangelical Congregational' = 'Evangelical Congregational',
  'Evangelical Covenant Church (covenant)' = 'Evangelical Covenant Church (covenant)',
  'Evangelical Free Baptist' = 'Evangelical Free Baptist',
  'Evangelical Free Church (free church)' = 'Evangelical Free Church (free church)',
  'Evangelical Lutheran Church in America' = 'Evangelical Lutheran Church in America',
  'Evangelical Methodist' = 'Evangelical Methodist',
  'Evangelical Reformed' = 'Evangelical Reformed',
  'Evangelical United Brethren' = 'Evangelical United Brethren',
  'Evangelical Presbyterian' = 'Evangelical Presbyterian',
  'Evangelical Pentecostal' = 'Evangelical Pentecostal',
  'Federated or union church' = 'Federated or union church',
  'Fire Baptized Holiness Church of God' = 'Fire Baptized Holiness Church of God',
  'Four Square Gospel (Four Square)' = 'Four Square Gospel (Four Square)',
  'Free Holiness' = 'Free Holiness',
  'Free Hungarian Reformed Church' = 'Free Hungarian Reformed Church',
  'Free Lutheran' = 'Free Lutheran',
  'Free Methodist Church' = 'Free Methodist Church',
  'Free Will Baptist' = 'Free Will Baptist',
  'Friends/Quaker, not further specified' = 'Friends/Quaker, not further specified',
  'Full Gospel' = 'Full Gospel',
  'Full Gospel Baptist' = 'Full Gospel Baptist',
  'Fundamentalist Baptist' = 'Fundamentalist Baptist',
  'Fundamentalist, not further specified' = 'Fundamentalist, not further specified',
  'General Association of Regular Baptist Churches' = 'General Association of Regular Baptist Churches',
  'Grace Brethren Church' = 'Grace Brethren Church',
  'Holiness Baptist' = 'Holiness Baptist',
  'Home church' = 'Home church',
  'Independent' = 'Independent',
  'Independent Baptist' = 'Independent Baptist',
  'Independent Catholic' = 'Independent Catholic',
  'Independent Congregational Church' = 'Independent Congregational Church',
  'Independent Holiness' = 'Independent Holiness',
  'Independent Methodist' = 'Independent Methodist',
  'Independent Presbyterian' = 'Independent Presbyterian',
  'Independent Reformed' = 'Independent Reformed',
  'Interdenominational' = 'Interdenominational',
  'International Baptist' = 'International Baptist',
  'International Communion of the Charismatic Episcopal Church' = 'International Communion of the Charismatic Episcopal Church',
  'International Pentecostal Church of Christ' = 'International Pentecostal Church of Christ',
  'Liberal/moderate nondenominational' = 'Liberal/moderate nondenominational',
  'Liberal/progressive Congregational' = 'Liberal/progressive Congregational',
  'Liberal/progressive/moderate Baptist' = 'Liberal/progressive/moderate Baptist',
  'Living Church of God' = 'Living Church of God',
  'Lutheran Brethren' = 'Lutheran Brethren',
  'Lutheran Church-Missouri Synod' = 'Lutheran Church-Missouri Synod',
  'Lutheran Church of the Reformation' = 'Lutheran Church of the Reformation',
  'Lutheran Church-Wisconsin Synod' = 'Lutheran Church-Wisconsin Synod',
  'Lutheran Congregations in Mission for Christ' = 'Lutheran Congregations in Mission for Christ',
  'Mennonite Brethren' = 'Mennonite Brethren',
  'Mennonite, not further specified' = 'Mennonite, not further specified',
  'Methodist Episcopal Church' = 'Methodist Episcopal Church',
  'Methodist Protestant Church' = 'Methodist Protestant Church',
  'Missionary Baptist' = 'Missionary Baptist',
  'Missionary Church' = 'Missionary Church',
  'Moravian Church' = 'Moravian Church',
  'National Association of Congregational Christian Churches' = 'National Association of Congregational Christian Churches',
  'National Baptist Convention' = 'National Baptist Convention',
  'New Testament Church of God' = 'New Testament Church of God',
  'Nondenominational charismatic' = 'Nondenominational charismatic',
  'Nondenominational Christian' = 'Nondenominational Christian',
  'Nondenominational evangelical' = 'Nondenominational evangelical',
  'Nondenominational fundamentalist' = 'Nondenominational fundamentalist',
  'Nondenominational, independent Pentecostal' = 'Nondenominational, independent Pentecostal',
  'Nondenominational Protestant' = 'Nondenominational Protestant',
  'North American Baptist' = 'North American Baptist',
  'North American Lutheran Church' = 'North American Lutheran Church',
  'not further specified' = 'not further specified',
  'Open Bible Standard Churches' = 'Open Bible Standard Churches',
  'Orthodox Presbyterian' = 'Orthodox Presbyterian',
  'Other' = 'Other',
  'Pentecostal Baptist' = 'Pentecostal Baptist',
  'Pentecostal Church of God' = 'Pentecostal Church of God',
  'Pentecostal Holiness Church' = 'Pentecostal Holiness Church',
  'Pilgrim Holiness Church' = 'Pilgrim Holiness Church',
  'Presbyterian Church in America' = 'Presbyterian Church in America',
  'Presbyterian Church of Canada' = 'Presbyterian Church of Canada',
  'Presbyterian Church (U.S.A.)' = 'Presbyterian Church (U.S.A.)',
  'Primitive Baptist' = 'Primitive Baptist',
  'Progressive Baptist Convention' = 'Progressive Baptist Convention',
  'Protestant Reformed Church' = 'Protestant Reformed Church',
  'Reformed Baptist (Calvinist)' = 'Reformed Baptist (Calvinist)',
  'Reformed Church in America (Dutch Reformed)' = 'Reformed Church in America (Dutch Reformed)',
  'Reformed Church in the United States (German Reformed)' = 'Reformed Church in the United States (German Reformed)',
  'Reformed Presbyterian' = 'Reformed Presbyterian',
  'Roman Catholic' = 'Roman Catholic',
  'Salvation Army' = 'Salvation Army',
  'Scotch Presbyterian' = 'Scotch Presbyterian',
  'Seventh-day Adventist' = 'Seventh-day Adventist',
  'Seventh Day Baptist' = 'Seventh Day Baptist',
  'Southern Baptist Convention' = 'Southern Baptist Convention',
  'Southern Methodist Church' = 'Southern Methodist Church',
  'Social Brethren' = 'Social Brethren',
  'Society of Friends' = 'Society of Friends',
  'Sovereign Grace' = 'Sovereign Grace',
  'Traditional Baptist' = 'Traditional Baptist',
  'United Baptist Church' = 'United Baptist Church',
  'United Brethren' = 'United Brethren',
  'United Church of Christ' = 'United Church of Christ',
  'United Church of God' = 'United Church of God',
  'United House of Prayer for All People' = 'United House of Prayer for All People',
  'United Methodist Church' = 'United Methodist Church',
  'United Pentecostal Church International' = 'United Pentecostal Church International',
  'United Presbyterian' = 'United Presbyterian',
  'Vineyard Fellowship' = 'Vineyard Fellowship',
  'Wesleyan Church' = 'Wesleyan Church',
  'Wesleyan Methodist Church' = 'Wesleyan Methodist Church',
  'Worldwide Church of God/Grace Communion International' = 'Worldwide Church of God/Grace Communion International',
}

export type SubDenomination = keyof typeof SubDenominations;
export const subDenominationsList = Object.keys(SubDenominations).sort() as SubDenomination[];

export type FullDenomination = `${Tradition} - ${SubDenomination}`;

export enum ProgramDimensions {
  FAITH = 'FAITH',
  HEALTH = 'HEALTH',
  FINANCES = 'FINANCES',
  RELATIONSHIPS = 'RELATIONSHIPS',
  OTHER = 'OTHER',
  VOCATION = 'VOCATION',
}

export enum ProgramKeys {
  MOPS = 'MOPS',
  CELEBRATE_RECOVERY = 'CELEBRATE_RECOVERY',
  FINANCIAL_PEACE_UNIVERSITY = 'FINANCIAL_PEACE_UNIVERSITY',
  PREPARE_ENRICH = 'PREPARE_ENRICH',
  RIGHTNOW_MEDIA = 'RIGHTNOW_MEDIA',
  SPIRITUAL_GIFTS_SURVEY = 'SPIRITUAL_GIFTS_SURVEY',
  ART_OF_MARRIAGE = 'ART_OF_MARRIAGE',
  MANAGING_OUR_FINANCES_GODS_WAY = 'MANAGING_OUR_FINANCES_GODS_WAY',
  ROOTED = 'ROOTED',
  MARRIED_PEOPLE = 'MARRIED_PEOPLE',
  STARTING_POINT = 'STARTING_POINT',
  RAMSEY_PLUS = 'RAMSEY_PLUS',
  COMMUNITY_BIBLE_STUDY = 'COMMUNITY_BIBLE_STUDY',
  LOVE_AND_RESPECT = 'LOVE_AND_RESPECT',
  ENNEAGRAM = 'ENNEAGRAM',
  MONEYLIFE = 'MONEYLIFE',
  WEEKEND_TO_REMEMBER = 'WEEKEND_TO_REMEMBER',
  MINISTRYGRID_LEADERSHIP = 'MINISTRYGRID_LEADERSHIP',
  CATHOLIC_MARRIAGE_PREP = 'CATHOLIC_MARRIAGE_PREP',
  UPWARD_SPORTS = 'UPWARD_SPORTS',
  ALPHA_MARRIAGE_COURSE = 'ALPHA_MARRIAGE_COURSE',
  REENGAGE = 'REENGAGE',
  OPEN_LIFECHURCH = 'OPEN_LIFECHURCH',
  TEN_GREAT_DATES = 'TEN_GREAT_DATES',
  FCA = 'FCA',
  YOUNG_LIFE = 'YOUNG_LIFE',
  ALL_PRO_DAD = 'ALL_PRO_DAD',
  HOLY_YOGA = 'HOLY_YOGA',
  GOOD_SENSE = 'GOOD_SENSE',
  TED_CUNNINGHAM_WHFC = 'TED_CUNNINGHAM_WHFC',
  DANIEL_PLAN = 'DANIEL_PLAN',
  NAVIGATORS = 'NAVIGATORS',
  CAPERNUM = 'CAPERNUM',
  WHOS_YOUR_ONE = 'WHOS_YOUR_ONE',
  PRECEPTS = 'PRECEPTS',
  FULLER_FORMATION_GROUPS = 'FULLER_FORMATION_GROUPS',
  MADE_TO_FLOURISH = 'MADE_TO_FLOURISH',
  A_LASTING_PROMISE = 'A_LASTING_PROMISE',
  MARRIAGE_ENCOUNTER = 'MARRIAGE_ENCOUNTER',
  STEPPING_UP = 'STEPPING_UP',
  ALL_AMERICA = 'ALL_AMERICA',
  SOUL_CARE = 'SOUL_CARE',
  WILD_AT_HEART = 'WILD_AT_HEART',
  REDEFINING_WORK_KELLER = 'REDEFINING_WORK_KELLER',
  EVERYCAMPUS = 'EVERYCAMPUS',
  TEENS_ENCOUNTER_CHRIST = 'TEENS_ENCOUNTER_CHRIST',
  OCEAN_PROGRAMS = 'OCEAN_PROGRAMS',
  FOCCUS = 'FOCCUS',
  TOTAL_LIFE_STEWARDSHIP = 'TOTAL_LIFE_STEWARDSHIP',
  TECH_WISE_FAMILY = 'TECH_WISE_FAMILY',
  DOWNLINE_DISCIPLESHIP = 'DOWNLINE_DISCIPLESHIP',
  DESTINY_FINDER = 'DESTINY_FINDER',
  WITNESS_TO_LOVE = 'WITNESS_TO_LOVE',
  PRE_CANA = 'PRE_CANA',
  KEEPING_MARRIAGES_HEALTHY = 'KEEPING_MARRIAGES_HEALTHY',
  COMEDY_DATE_NIGHT = 'COMEDY_DATE_NIGHT',
  GOD_PROVIDES = 'GOD_PROVIDES',
  ESSENTIALS_OF_BIBLICAL_STEWARDSHIP = 'ESSENTIALS_OF_BIBLICAL_STEWARDSHIP',
  HOPE_FILLED_LIVING_TURBULENT_TIMES = 'HOPE_FILLED_LIVING_TURBULENT_TIMES',
  SYMBIS = 'SYMBIS',
  YOUVERSION = 'YOUVERSION',
  ORANGE = 'ORANGE',
  BIBLE_STUDY_FELLOWSHIP = 'BIBLE_STUDY_FELLOWSHIP',
  ALPHA = 'ALPHA',
  PURPOSE_DRIVEN_LIFE = 'PURPOSE_DRIVEN_LIFE',
  BIBLE_PROJECT = 'BIBLE_PROJECT',
  SMALLGROUP_DOT_COM = 'SMALLGROUP_DOT_COM',
  AWANA = 'AWANA',
  ART_OF_PARENTING = 'ART_OF_PARENTING',
  LIVING_YOUR_STRENGTHS = 'LIVING_YOUR_STRENGTHS',
  LOVE_AND_LOGIC = 'LOVE_AND_LOGIC',
  BLESS_EVERY_HOME = 'BLESS_EVERY_HOME',
  SMART_STEPFAMILY = 'SMART_STEPFAMILY',
  GODS_WORD_FOR_WORK = 'GODS_WORD_FOR_WORK',
  AMERICA_PRAYS = 'AMERICA_PRAYS',
  DO_WELL = 'DO_WELL',
  RISE = 'RISE',
  VERTICAL_MARRIAGE = 'VERTICAL_MARRIAGE',
  TRUMOTIVATE = 'TRUMOTIVATE',
  FAITH_AND_CO = 'FAITH_AND_CO',
  LIVING_WATERS = 'LIVING_WATERS',
  NAVIGATOR_TRAINING = 'NAVIGATOR_TRAINING',
  CONVOY_OF_HOPE = 'CONVOY_OF_HOPE',
}

export const ProgramKeysList = Object.keys(ProgramKeys).sort() as ProgramKeys[];

export const Programs: { [key in ProgramKeys]: string } = {
  [ProgramKeys.MOPS]: 'MOPS',
  [ProgramKeys.CELEBRATE_RECOVERY]: 'Celebrate Recovery',
  [ProgramKeys.FINANCIAL_PEACE_UNIVERSITY]: 'Financial Peace University',
  [ProgramKeys.PREPARE_ENRICH]: 'Prepare/Enrich',
  [ProgramKeys.RIGHTNOW_MEDIA]: 'RightNow Media (spans all)',
  [ProgramKeys.SPIRITUAL_GIFTS_SURVEY]: 'Spiritual Gifts Survey (Lifeway)',
  [ProgramKeys.ART_OF_MARRIAGE]: 'The Art of Marriage',
  [ProgramKeys.MANAGING_OUR_FINANCES_GODS_WAY]: "Managing our Finances God's Way",
  [ProgramKeys.ROOTED]: 'Rooted',
  [ProgramKeys.MARRIED_PEOPLE]: 'Married People',
  [ProgramKeys.STARTING_POINT]: 'Starting Point',
  [ProgramKeys.RAMSEY_PLUS]: 'Ramsey+',
  [ProgramKeys.COMMUNITY_BIBLE_STUDY]: 'Community Bible Study',
  [ProgramKeys.LOVE_AND_RESPECT]: 'Love & Respect',
  [ProgramKeys.ENNEAGRAM]: 'Enneagram',
  [ProgramKeys.MONEYLIFE]: 'MoneyLife',
  [ProgramKeys.WEEKEND_TO_REMEMBER]: 'Weekend to Remember',
  [ProgramKeys.MINISTRYGRID_LEADERSHIP]: 'MinistryGrid - leadership',
  [ProgramKeys.CATHOLIC_MARRIAGE_PREP]: 'Catholic Marriage Prep',
  [ProgramKeys.UPWARD_SPORTS]: 'Upward Sports',
  [ProgramKeys.ALPHA_MARRIAGE_COURSE]: 'Alpha Marriage Course',
  [ProgramKeys.REENGAGE]: 'ReEngage',
  [ProgramKeys.OPEN_LIFECHURCH]: 'OPEN (LifeChurch)',
  [ProgramKeys.TEN_GREAT_DATES]: '10 Great Dates',
  [ProgramKeys.FCA]: 'Fellowship of Christian Athletes',
  [ProgramKeys.YOUNG_LIFE]: 'Young Life',
  [ProgramKeys.ALL_PRO_DAD]: 'All Pro Dad',
  [ProgramKeys.HOLY_YOGA]: 'Holy Yoga',
  [ProgramKeys.GOOD_SENSE]: 'Good Sense',
  [ProgramKeys.TED_CUNNINGHAM_WHFC]: 'Ted Cunningham / WHFC',
  [ProgramKeys.DANIEL_PLAN]: 'Daniel Plan',
  [ProgramKeys.NAVIGATORS]: 'Navigators 2:7',
  [ProgramKeys.CAPERNUM]: 'Capernum',
  [ProgramKeys.WHOS_YOUR_ONE]: "Who's Your One?",
  [ProgramKeys.PRECEPTS]: 'Precepts',
  [ProgramKeys.FULLER_FORMATION_GROUPS]: 'Fuller Formation Groups',
  [ProgramKeys.MADE_TO_FLOURISH]: 'Made to Flourish',
  [ProgramKeys.A_LASTING_PROMISE]: 'A Lasting Promise',
  [ProgramKeys.MARRIAGE_ENCOUNTER]: 'Marriage Encounter',
  [ProgramKeys.STEPPING_UP]: 'Stepping Up',
  [ProgramKeys.ALL_AMERICA]: 'All America',
  [ProgramKeys.SOUL_CARE]: 'Soul Care',
  [ProgramKeys.WILD_AT_HEART]: 'Wild at Heart',
  [ProgramKeys.REDEFINING_WORK_KELLER]: 'Redefining Work (Keller)',
  [ProgramKeys.EVERYCAMPUS]: 'EveryCampus',
  [ProgramKeys.TEENS_ENCOUNTER_CHRIST]: 'Teens Encounter Christ (TEC)',
  [ProgramKeys.OCEAN_PROGRAMS]: 'OCEAN Programs',
  [ProgramKeys.FOCCUS]: 'FOCCUS',
  [ProgramKeys.TOTAL_LIFE_STEWARDSHIP]: 'Total Life Stewardship',
  [ProgramKeys.TECH_WISE_FAMILY]: 'Tech-Wise Family',
  [ProgramKeys.DOWNLINE_DISCIPLESHIP]: 'Downline Discipleship',
  [ProgramKeys.DESTINY_FINDER]: 'Destiny Finder',
  [ProgramKeys.WITNESS_TO_LOVE]: 'Witness to Love',
  [ProgramKeys.PRE_CANA]: 'Pre Cana',
  [ProgramKeys.KEEPING_MARRIAGES_HEALTHY]: 'Keeping Marriages Healthy',
  [ProgramKeys.COMEDY_DATE_NIGHT]: 'Comedy Date Night',
  [ProgramKeys.GOD_PROVIDES]: 'God Provides',
  [ProgramKeys.ESSENTIALS_OF_BIBLICAL_STEWARDSHIP]: 'Essentials of Biblical Stewardship',
  [ProgramKeys.HOPE_FILLED_LIVING_TURBULENT_TIMES]: 'Hope-Filled Living in Turbulent Times',
  [ProgramKeys.SYMBIS]: 'Symbis',
  [ProgramKeys.YOUVERSION]: 'YouVersion',
  [ProgramKeys.ORANGE]: 'Orange',
  [ProgramKeys.BIBLE_STUDY_FELLOWSHIP]: 'Bible Study Fellowship',
  [ProgramKeys.ALPHA]: 'Alpha',
  [ProgramKeys.PURPOSE_DRIVEN_LIFE]: 'The Purpose Driven Life',
  [ProgramKeys.BIBLE_PROJECT]: 'The Bible Project',
  [ProgramKeys.SMALLGROUP_DOT_COM]: 'Smallgroup.com (Lifeway)',
  [ProgramKeys.AWANA]: 'Awana',
  [ProgramKeys.ART_OF_PARENTING]: 'The Art of Parenting',
  [ProgramKeys.LIVING_YOUR_STRENGTHS]: 'Living Your Strengths',
  [ProgramKeys.LOVE_AND_LOGIC]: 'Love & Logic',
  [ProgramKeys.BLESS_EVERY_HOME]: 'Bless Every Home',
  [ProgramKeys.SMART_STEPFAMILY]: 'The Smart Stepfamily',
  [ProgramKeys.GODS_WORD_FOR_WORK]: "God's Word for Work",
  [ProgramKeys.AMERICA_PRAYS]: 'America Prays',
  [ProgramKeys.DO_WELL]: 'Do Well',
  [ProgramKeys.RISE]: 'Rise (Dee Eastman)',
  [ProgramKeys.VERTICAL_MARRIAGE]: 'Vertical Marriage',
  [ProgramKeys.TRUMOTIVATE]: 'TruMotivate',
  [ProgramKeys.FAITH_AND_CO]: 'Faith & Co (Seattle Pacific U)',
  [ProgramKeys.LIVING_WATERS]: 'Living Waters',
  [ProgramKeys.NAVIGATOR_TRAINING]: 'Navigator Training',
  [ProgramKeys.CONVOY_OF_HOPE]: 'Convoy of Hope',
};

export const ProgramDimensionsPrograms: { [key in ProgramDimensions]: Array<ProgramKeys> } = {
  [ProgramDimensions.FAITH]: [
    ProgramKeys.MOPS,
    ProgramKeys.SPIRITUAL_GIFTS_SURVEY,
    ProgramKeys.ROOTED,
    ProgramKeys.COMMUNITY_BIBLE_STUDY,
    ProgramKeys.OPEN_LIFECHURCH,
    ProgramKeys.YOUNG_LIFE,
    ProgramKeys.NAVIGATORS,
    ProgramKeys.CAPERNUM,
    ProgramKeys.WHOS_YOUR_ONE,
    ProgramKeys.PRECEPTS,
    ProgramKeys.FULLER_FORMATION_GROUPS,
    ProgramKeys.ALL_AMERICA,
    ProgramKeys.SOUL_CARE,
    ProgramKeys.EVERYCAMPUS,
    ProgramKeys.TEENS_ENCOUNTER_CHRIST,
    ProgramKeys.DOWNLINE_DISCIPLESHIP,
    ProgramKeys.YOUVERSION,
    ProgramKeys.BIBLE_STUDY_FELLOWSHIP,
    ProgramKeys.ALPHA,
    ProgramKeys.BIBLE_PROJECT,
    ProgramKeys.SMALLGROUP_DOT_COM,
    ProgramKeys.AWANA,
    ProgramKeys.BLESS_EVERY_HOME,
    ProgramKeys.AMERICA_PRAYS,
  ],
  [ProgramDimensions.HEALTH]: [
    ProgramKeys.CELEBRATE_RECOVERY,
    ProgramKeys.HOLY_YOGA,
    ProgramKeys.DANIEL_PLAN,
    ProgramKeys.WILD_AT_HEART,
    ProgramKeys.HOPE_FILLED_LIVING_TURBULENT_TIMES,
    ProgramKeys.RISE,
    ProgramKeys.LIVING_WATERS,
    ProgramKeys.NAVIGATOR_TRAINING,
  ],
  [ProgramDimensions.FINANCES]: [
    ProgramKeys.FINANCIAL_PEACE_UNIVERSITY,
    ProgramKeys.MANAGING_OUR_FINANCES_GODS_WAY,
    ProgramKeys.RAMSEY_PLUS,
    ProgramKeys.MONEYLIFE,
    ProgramKeys.GOOD_SENSE,
    ProgramKeys.TOTAL_LIFE_STEWARDSHIP,
    ProgramKeys.GOD_PROVIDES,
    ProgramKeys.ESSENTIALS_OF_BIBLICAL_STEWARDSHIP,
    ProgramKeys.DO_WELL,
  ],
  [ProgramDimensions.RELATIONSHIPS]: [
    ProgramKeys.PREPARE_ENRICH,
    ProgramKeys.ART_OF_MARRIAGE,
    ProgramKeys.MARRIED_PEOPLE,
    ProgramKeys.STARTING_POINT,
    ProgramKeys.LOVE_AND_RESPECT,
    ProgramKeys.WEEKEND_TO_REMEMBER,
    ProgramKeys.CATHOLIC_MARRIAGE_PREP,
    ProgramKeys.ALPHA_MARRIAGE_COURSE,
    ProgramKeys.REENGAGE,
    ProgramKeys.TEN_GREAT_DATES,
    ProgramKeys.ALL_PRO_DAD,
    ProgramKeys.TED_CUNNINGHAM_WHFC,
    ProgramKeys.A_LASTING_PROMISE,
    ProgramKeys.MARRIAGE_ENCOUNTER,
    ProgramKeys.STEPPING_UP,
    ProgramKeys.SOUL_CARE,
    ProgramKeys.FOCCUS,
    ProgramKeys.TECH_WISE_FAMILY,
    ProgramKeys.WITNESS_TO_LOVE,
    ProgramKeys.PRE_CANA,
    ProgramKeys.KEEPING_MARRIAGES_HEALTHY,
    ProgramKeys.COMEDY_DATE_NIGHT,
    ProgramKeys.SYMBIS,
    ProgramKeys.ORANGE,
    ProgramKeys.ART_OF_PARENTING,
    ProgramKeys.LOVE_AND_LOGIC,
    ProgramKeys.SMART_STEPFAMILY,
    ProgramKeys.VERTICAL_MARRIAGE,
  ],
  [ProgramDimensions.OTHER]: [
    ProgramKeys.RIGHTNOW_MEDIA,
    ProgramKeys.ENNEAGRAM,
    ProgramKeys.MINISTRYGRID_LEADERSHIP,
    ProgramKeys.UPWARD_SPORTS,
    ProgramKeys.FCA,
    ProgramKeys.CONVOY_OF_HOPE,
  ],
  [ProgramDimensions.VOCATION]: [
    ProgramKeys.MADE_TO_FLOURISH,
    ProgramKeys.REDEFINING_WORK_KELLER,
    ProgramKeys.OCEAN_PROGRAMS,
    ProgramKeys.DESTINY_FINDER,
    ProgramKeys.PURPOSE_DRIVEN_LIFE,
    ProgramKeys.LIVING_YOUR_STRENGTHS,
    ProgramKeys.GODS_WORD_FOR_WORK,
    ProgramKeys.TRUMOTIVATE,
    ProgramKeys.FAITH_AND_CO,
  ],
};

export type OrganizationProfile = {
  tradition?: Tradition | null;
  subDenomination?: SubDenomination | null;
  fullDenomination?: FullDenomination | null;
  statementOfFaithUrl?: string;
  staffSize?: string;
  ministries?: MinistryKeys[];
  programs?: ProgramKeys[];
};

export type AuthenticityStatus =
  | 'unknown'
  | 'glooVerificationRequested'
  | 'glooReVerificationRequired'
  | 'glooLegacyVerified'
  | 'glooAdminRejected'
  | 'glooAdminVerified'
  | 'glooAutoRejected'
  | 'glooAutoVerified'
  | 'glooGovernmentIdVerified'
  | 'glooGovernmentIdPending';

export interface Organizations extends AuditableEntity {
  id: number;
  name: string;
  status: OrgStatus;
  config: OrgConfig;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  claimedPhoneDate?: Date;
  authenticityStatus?: AuthenticityStatus;
  actions?: Actions[];
  activities?: Activities[];
  broadcasts?: Broadcasts[];
  devotionals?: Devotionals[];
  prayerRequests?: PrayerRequests[];
  users?: Users[];
  invitations?: Invitations[];
  stories?: Stories[];
  devoSubscribers?: DevoSubscribers[];
  integrations?: Integrations[];
  contextConfigs?: ContextConfigs[];
  userDataCaptures?: UserDataCaptures[];
  referenceLists?: ReferenceLists[];
  keywords?: Keywords[];
  effects?: Effects[];
  msgCounters?: MsgCounter[];
  subscription?: Subscriptions;
  transactions?: Transactions[];
  orgTemplates?: OrgTemplates[];
  orgKits?: OrgKits[];
  orgHubs?: OrgHubs[];
  verifications?: Verifications[];
  messageLogs?: MessageLogs[];
  orgUserActivities?: OrgUserActivities[];
  assessmentsResults?: AssessmentsResults[];
  msgTotals?: MsgTotals[];
  orgRewards?: OrgRewards[];
  referralCodeId?: string;
  referralCode?: RewardCodes;
  rewardCodes?: RewardCodes[];
  explorers?: Explorers[];
  explorerConnections?: ExplorerConnections[];
  explorerSettings?: ExplorerSettings;
  location?: any;
  profile?: OrganizationProfile;
  explorerConnectionsInPastWeek?: number;
  explorerConnectionsCount?: number;
  orgUserPermissions?: OrgUserPermissions[];
  orgInvitations?: OrganizationInvitations[];
  allowedExplorerCampaignIds?: string[];
  campaignsArr?: ExplorerCampaigns[];
  isAuthenticVerified: boolean;
  isAuthenticRejected: boolean;
  isAwaitingAuthenticityCheck: boolean;
  parentOrganizationId?: number;
}

export interface OrgUsers extends AuditableEntity {
  id: number;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  imageUrl?: string;
  access: OrgUserAccess;
  extra: OrgUserExtra;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  respondedToPrayerRequests?: PrayerRequests[];
  orgUsersToUsersAssignments?: OrgUsersToUsersAssignments[];
  unresolvedMessageCount?: number;
  orgUserPermissions?: OrgUserPermissions[];
  sessions?: SessionEntity[];
}

export interface OrgUsersToUsersAssignments {
  id: string;
  orgUserId: number;
  userId: number;
  auditVersion: number;
  createdAt: Date;
  orgUser?: OrgUsers;
  user?: Users;
}

export interface OrgUsersToMessageAssignments {
  auditVersion: number;
  createdAt: Date;
  id: string;
  messageTimestamp: Date;
  organization?: Organizations;
  orgId: number;
  orgUser?: OrgUsers;
  orgUserId: number;
  user?: Users;
  userId: number;
}

export interface OrgBillingDetails {
  email: string;
  name: string;
  phone: string;
}

export interface OrgPaymentMethodCardDetails {
  brand: string;
  expMonth: number;
  expYear: number;
  last4: string;
}

export interface OrgPaymentMethod {
  id: string;
  billingDetails: OrgBillingDetails;
  card: OrgPaymentMethodCardDetails;
  type: string;
}

export interface PrayerRequests {
  id: number;
  text: string;
  responseText: UserMessageI18n;
  respondTime?: Date;
  userId?: number;
  assignedToOrgUsers: number[];
  respondedToByOrgUserId?: number;
  platform: PlatformType;
  status: PrayerRequestStatus;
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  user?: Users;
  respondedToByOrgUser?: OrgUsers;
  assignedToOrgUsersArr?: OrgUsers[];
}

export interface ReferenceLists {
  id: number;
  type: ReferenceListTypesStrings;
  value: string;
  title: string;
  info: ReferenceListInfo;
  orgId?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
}

export interface ReachTopicPreferences {
  faith: boolean;
  relationship: boolean;
  vocation: boolean;
  finances: boolean;
  mentalWellBeing: boolean;
  prayerRequests: boolean;
}

export interface OrgProgramsAndServices {
  campusMinistry: boolean;
  crisis: boolean;
  evangelistic: boolean;
  online: boolean;
  prayerMinistry: boolean;
}
export enum ReferenceListTypes {
  GROUPS = 'GROUPS',
  TAGS = 'TAGS',
  TOPICS = 'CONVERSATION_PROMPT_TOPICS',
}

export type ReferenceListTypesStrings = 'GROUPS' | 'TAGS' | 'CONVERSATION_PROMPT_TOPICS';

export interface Users {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  platform: PlatformType;
  smsNumber?: string;
  status: UserStatus;
  info: UserInfo;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  conversationStatus?: UserConversationStatus;
  orgId: number;
  organization?: Organizations;
  prayerRequests?: PrayerRequests[];
  stories?: Stories[];
  groupsArr?: ReferenceLists[];
  usersToGroups?: UsersToGroups[];
  activities?: Activities[];
  devotionalSubscriptions?: DevoSubscribers[];
  userDataCaptures?: UserDataCaptures[];
  orgUsersToUsersAssignments?: OrgUsersToUsersAssignments[];
  messageLogs?: MessageLogs[];
  orgUserActivities?: OrgUserActivities[];
  explorers?: Explorers[];
  explorerConnection?: ExplorerConnections;
}

export type CreateUsersData = {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  address?: string;
  smsNumber?: string;
  groupIds?: number[];
};

export type UserWithGroupIds = Partial<Users> & { groupIds?: number[] };
export interface UsersToGroups {
  userId: number;
  groupId: number;
  createdAt: Date;
  user?: Users;
  group?: ReferenceLists;
}

export interface Keywords {
  id: number;
  text: string;
  orgId: number;
  smsSharedNumber?: string;
  actionId?: number;
  devotionalId?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  action?: Actions;
  devotional?: Devotionals;
}

export interface Stories {
  id: number;
  title?: string;
  images: string[];
  video?: string;
  userId: number;
  content?: string;
  tags: number[];
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  tagsArr?: ReferenceLists[];
  user?: Users;
}

export interface Invitations {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  token: string;
  orgId?: number;
  orgInvitationId?: string;
  permissions: Permissions[];
  orgUserPermissions?: Partial<OrgUserPermissions>;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  orgInvitation?: OrganizationInvitations;
  expirationDate: Date;
}

export interface Notes {
  id: string;
  text: string;
  userId?: number;
  explorerConnectionId?: string;
  creatorOrgUserId: number;
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  creatorOrgUser?: OrgUsers;
  user?: Users;
  organization?: Organizations;
  explorerConnection?: ExplorerConnections;
}

export interface NotesCreateParams {
  text: string;
  userId?: number;
  explorerConnectionId?: string;
  creatorOrgUserId: number;
  orgId: number;
  auditVersion: number;
  creatorOrgUser?: OrgUsers;
  user?: Users;
  organization?: Organizations;
  explorerConnection?: ExplorerConnections;
}

export type NotesUpdateParams = {
  text?: string;
  userId?: number;
  explorerConnectionId?: string;
  creatorOrgUserId?: number;
  orgId?: number;
  auditVersion?: number;
  creatorOrgUser?: OrgUsers;
  user?: Users;
  organization?: Organizations;
  explorerConnection?: ExplorerConnections;
};

export type VerificationStatus = 'verified' | 'pending';

export interface Verifications {
  id: string;
  status: VerificationStatus;
  phoneNumber: string;
  code: string;
  orgId: number;
  orgUserId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  deletedAt?: Date;
}

// Updating this table only ever happens in a cron job when we're sending out a message and calculating the next date
export interface DevoSubscribers {
  id: number;
  orgId: number;
  userId: number;
  devotionalId: number;
  status: DevoSubscriberStatus;
  nextMessageDateTime?: Date;
  nextMessageIndex?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  user?: Users;
  devotional?: Devotionals;
}

export interface Integrations {
  id: number;
  type: IntegrationsType;
  accountId: string;
  accountUser: IntegrationsAccountUser;
  info: IntegrationsInfo;
  sync: IntegrationsSync;
  orgId: number;
  integrationConfigId?: string;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
}

export interface Plans {
  id: number;
  status: DefaultStatus;
  title: string;
  description?: string;
  info: PlanInfo;
  billingPeriodType: BillingPeriodType;
  internalIdentifier?: string;
  productsAllowedIds: string[];
  productsRequiredIds: string[];
  productsIncludedIds: string[];
  productsAllowed: Products[];
  productsRequired: Products[];
  productsIncluded: Products[];
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organizations?: Organizations[];
  subscriptions?: Subscriptions[];
  transactions?: Transactions[];
  expireFallbackPlan?: Plans;
}

export interface ContextConfigs {
  id: number;
  title: string;
  type: ContextConfigTypesString;
  status: ContextConfigStatus;
  repeat: ContextConfigRepeat;
  content: ContextConfigContent;
  groups: number[];
  users: number[];
  optOutUsers: number[];
  orgId: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  broadcasts?: Broadcasts[];
  userDataCaptures?: UserDataCaptures[];
  groupsArr?: ReferenceLists[];
  usersArr?: Users[];
  optOutUsersArr?: Users[];
}

export interface UserDataCaptures {
  id: number;
  orgId: number;
  response: string;
  valueNum?: number;
  valueStr?: string;
  userId: number;
  contextConfigId?: number;
  info: UserDataCaptureInfo;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  user?: Users;
  contextConfig?: ContextConfigs;
}

export interface Effects {
  id: number;
  instructions: EffectInstructions;
  orgId: number;
  actionId?: number;
  devotionalId?: number;
  createdAt: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  action?: Actions;
  devotional?: Devotionals;
}

export interface Transactions {
  id: string;
  orgId: number;
  subscriptionId?: string;
  productId?: string;
  quantity?: number;
  info: TransactionInfo;
  stripeInvoiceId?: string;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
  subscription?: Subscriptions;
  product?: Products;
  msgTotals?: MsgTotals;
}

export interface Subscriptions {
  id: string;
  orgId: number;
  nextBillingDate?: Date;
  status: SubscriptionStatus;
  trialEndDate?: Date;
  info: SubscriptionInfo;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
  subscriptionProducts?: SubscriptionProducts[];
  transactions?: Transactions[];
}

export enum ProductFeatureKeys {
  SMS_CREDITS = 'SMS_CREDITS',
  SMS_UNLIMITED = 'SMS_UNLIMITED',
  EXPLORERS = 'EXPLORERS',
  PLAN = 'PLAN',
  BARNA = 'BARNA',
}

export interface Products {
  id: string;
  name: string;
  billingPeriodType?: BillingPeriodType | null;
  unitQuantity?: number;
  info: ProductInfo;
  type: ProductType;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  featureKey?: ProductFeatureKeys;
  quantityOptions: number[];
  reload: boolean;
  subscriptionProducts?: SubscriptionProducts[];
  transactions?: Transactions[];
  trialPeriodDays?: number;
}

export interface ProductsFindByFeatureKeyQuery {
  featureKeys?: ProductFeatureKeys[];
}

export interface OnboardingFlowsDesign {
  topLogoUrl?: string;
  heroImgUrl?: string;
  title?: string;
  description?: string;
  leadInTitle?: string;
  leadInDescription?: string;
  formFieldsTitleText?: string;
  doorway?: string;
}

export interface OnboardingFlows {
  id: string;
  key: string;
  selectedPlanId?: number;
  planIds: number[];
  selectedProducts?: { productId: string; selectedQuantity?: number }[];
  appliedPromoCodes?: string[];
  design: OnboardingFlowsDesign;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  plans: Plans[];
  landingUrl?: string;
}

export type MsgCounterMsgType = 'smsin' | 'smsout';

export interface MsgCounter {
  id: string;
  type: MsgCounterMsgType;
  value: number;
  orgDate: string; // 'yyyy-mm-dd' format
  orgId: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
}

export interface MsgTotals {
  id: string;
  orgId: number;
  transactionId: string;
  info: MsgTotalsInfo;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
  transaction?: Transactions;
}

export interface SubscriptionProducts {
  id: string;
  subscriptionId: string;
  productId: string;
  quantity: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  subscription?: Subscriptions;
  product?: Products;
  trialEndDate?: Date;
  trialStartDate?: Date;
  expiredDate?: Date;
  cancelledDate?: Date;
  activeAtDate?: Date;
}

export interface AssessmentsResults {
  id: string;
  results: RciCalculations;
  resultNumber?: number;
  deploymentSessionId: string;
  userToken: string;
  userId?: number;
  orgId?: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  organization?: Organizations;
  user?: Users;
}

export interface Templates {
  id: string;
  type: TemplateType;
  title: string;
  shortDescription: string;
  longDescription: string;
  thumbnailImageUrl?: string;
  imageUrl?: string;
  config: TemplateConfig;
  metadata: TemplateMetadata;
  visibleInDiscover: boolean;
  orgTypeFiltering: OrgTypeFiltering;
  tags: string[];
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  archivedAt?: Date;
}

export interface TemplateCreateParams {
  type: TemplateType;
  title: string;
  shortDescription: string;
  longDescription: string;
  thumbnailImageUrl?: string;
  imageUrl?: string;
  config: TemplateConfig;
  metadata: TemplateMetadata;
  tags: string[];
  visibleInDiscover: boolean;
  orgTypeFiltering?: OrgTypeFiltering;
}

export interface TemplateUpdateParams {
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  thumbnailImageUrl?: string;
  imageUrl?: string;
  config?: TemplateConfig;
  metadata?: TemplateMetadata;
  tags?: string[];
  visibleInDiscover?: boolean;
  orgTypeFiltering?: OrgTypeFiltering;
}

export type KitResourceId = {
  id: string;
  type: DiscoverItemType.TEMPLATE | DiscoverItemType.KIT;
};

export type KitResource = {
  type: DiscoverItemType.TEMPLATE | DiscoverItemType.KIT;
  kit?: Kits;
  template?: Templates;
};

export interface Kits {
  id: string;
  title: string;
  subtitle?: string;
  shortDescription: string;
  longDescription: string;
  instructions: string;
  thumbnailImageUrl?: string;
  bannerImageUrl?: string;
  // TODO: Deprecate templateIds and templates once the old kits are updated
  templateIds: string[];
  templates: Templates[];
  // TODO: Remove optional field around kitResourceIds and kitResources once old kits are updated
  kitResourceIds?: KitResourceId[];
  kitResources?: KitResource[];
  // TODO: Deprecate resources once the old kits are updated
  resources: PlaybookResource[];
  tags: string[];
  auditVersion: number;
  metadata: KitMetadata;
  branding?: ChampionBranding;
  visibleInDiscover?: boolean;
  orgTypeFiltering: OrgTypeFiltering;
  featuredPriority?: number;
  orgKit?: OrgKits;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  archivedAt?: Date;
}

export type LoadedKit = Kits & { kitResources: KitResource[] };

export type HexDigit =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F';
export type HexColor<T extends string> = T extends `#${HexDigit}${HexDigit}${HexDigit}${infer Rest1}`
  ? Rest1 extends ``
    ? T // three-digit hex color
    : Rest1 extends `${HexDigit}${HexDigit}${HexDigit}`
      ? T // six-digit hex color
      : never
  : never;

// eslint-disable-next-line @typescript-eslint/naming-convention
export type Color = string & { __type: 'HexColor' };

export type ChampionBranding = {
  backgroundColor?: Color;
  textColor?: Color;
  titleColor?: Color;
  logoImageUrl?: string;
};

export type OrgHubs = {
  id: string;
  orgId: number;
  hubId: string;
  hub?: Hubs;
  favorited: boolean;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
};

export type HubMetadata = {
  resourceCount?: number;
  author?: string;
  favorited?: boolean;
  orgHubId?: string;
};

export type OrgTypeFiltering = 'church' | 'recovery' | 'all';

export type Hubs = {
  id: string;
  author: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  backgroundColor: string;
  titleColor?: string;
  descriptionColor?: string;
  logoImageUrl: string;
  thumbnailImageUrl: string;
  headerImageUrl: string;
  sections: HubSection[];
  tags: string[];
  auditVersion: number;
  visibleInDiscover: boolean;
  orgTypeFiltering: OrgTypeFiltering;
  featuredPriority?: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  archivedAt?: Date;
  orgHub?: OrgHubs;
};

export type HubUpsertParams = Omit<
  Hubs,
  | 'id'
  | 'featuredPriority'
  | 'createdAt'
  | 'updatedAt'
  | 'deletedAt'
  | 'archivedAt'
  | 'orgHub'
  | 'auditVersion'
  | 'sections'
> & { sections: HubSectionUpsertParams[] };

export type HubSection = HubImageSection | HubTextSection | HubResourceSection | HubSplitSection;

export type HubSectionUpsertParams =
  | HubImageSection
  | HubTextSection
  | HubResourceSectionUpsertParams
  | HubSplitSection;

export type HubImageSection = HubSectionCommonalities & HubImageSectionSpecifics;

export type HubTextSection = HubSectionCommonalities & HubTextSectionSpecifics;

export type HubResourceSection = HubSectionCommonalities & HubResourceSectionSpecifics;

export type LoadedHubResourceSection = HubResourceSection & { resources: HubResource[] };

export type HubResourceSectionUpsertParams = HubSectionCommonalities & HubResourceSectionUpsertParamsSpecifics;

export type HubSplitSection = HubSectionCommonalities & HubSplitSectionSpecifics;

type HubSectionCommonalities = {
  title?: string;
  description?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  titleColor?: string;
  descriptionColor?: string;
};

type HubImageSectionSpecifics = {
  type: HubSectionType.IMAGE;
  imageUrl: string | null;
  linkUrl?: string | null;
};

type HubTextSectionSpecifics = {
  type: HubSectionType.TEXT;
  text: string;
  textColor?: string;
};

type HubResourceSectionSpecifics = {
  type: HubSectionType.RESOURCE;
  resourceIds: HubResourceId[];
  resources?: HubResource[];
};

type HubResourceSectionUpsertParamsSpecifics = Omit<HubResourceSectionSpecifics, 'resources'>;

type HubSplitSectionSpecifics = {
  type: HubSectionType.SPLIT;
  a: HubSplitSectionContent;
  b: HubSplitSectionContent;
};

export enum HubSectionType {
  IMAGE = 'image',
  TEXT = 'text',
  RESOURCE = 'resource',
  SPLIT = 'split',
}

export type HubResourceId = {
  id: string;
  type: DiscoverItemType.KIT | DiscoverItemType.TEMPLATE;
};

export type HubResource =
  | {
      type: DiscoverItemType.KIT;
      kit: Kits;
    }
  | {
      type: DiscoverItemType.TEMPLATE;
      template: Templates;
    };

export type HubSplitSectionContent = HubSplitSectionImageContent | HubSplitSectionTextContent;

export type HubSplitSectionImageContent = {
  type: HubSplitSectionContentType.IMAGE;
  imageUrl: string | null;
  linkUrl?: string | null;
};

export type HubSplitSectionTextContent = {
  type: HubSplitSectionContentType.TEXT;
  text: string;
  textColor?: string;
};

export enum HubSplitSectionContentType {
  IMAGE = 'image',
  TEXT = 'text',
}

export type KitMetadata = {
  templateCount?: number;
  resourceCount?: number;
  kitResourceCount?: number;
  favorited?: boolean;
  orgKitId?: string;
  author?: string;
  topics?: string[];
};

export type KitCreateParams = {
  title: string;
  subtitle?: string;
  shortDescription: string;
  longDescription: string;
  instructions: string;
  thumbnailImageUrl?: string;
  bannerImageUrl?: string;
  // TODO: Deprecate templateIds once Kits are updated
  templateIds: string[];
  tags: string[];
  // TODO: Remove optional once Kits are updated
  kitResourceIds?: KitResourceId[];
  // TODO: Deprecate resources once Kits are updated
  resources: PlaybookResource[];
  metadata?: KitMetadata;
  visibleInDiscover?: boolean;
  orgTypeFiltering?: OrgTypeFiltering;
  featuredPriority?: number;
  auditVersion: number;
};

export type KitUpdateParams = {
  title?: string;
  subtitle?: string;
  shortDescription?: string;
  longDescription?: string;
  instructions?: string;
  thumbnailImageUrl?: string;
  bannerImageUrl?: string;
  // TODO: Deprecate templateIds once Kits are updated
  templateIds?: string[];
  tags?: string[];
  kitResourceIds?: KitResourceId[];
  // TODO: Deprecate resources once Kits are updated
  resources?: PlaybookResource[];
  metadata?: KitMetadata;
  visibleInDiscover?: boolean;
  orgTypeFiltering?: OrgTypeFiltering;
  featuredPriority?: number;
  auditVersion?: number;
};

export type SuccessResult = {
  success: boolean;
};

export type AuthSuccessResult = {
  success: boolean;
  accountExists?: boolean;
  idToken?: string;
};

export interface OrgTemplates {
  id: string;
  orgId: number;
  templateId: string;
  orgKitId?: string;
  contentRecordType: TemplateType;
  contentRecordId: number;
  creatorId: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface OrgKits {
  id: string;
  orgId: number;
  kitId: string;
  kit?: Kits;
  favorited: boolean;
  templateCompletion: OrgKitTemplateCompletion;
  creatorId: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export enum MessageDirection {
  In = 'in',
  Out = 'out',
}

export interface MessageLogs {
  id: string;
  orgId: number;
  userId: number;
  message: string;
  direction?: MessageDirection;
  provider?: OrgConfig['provider'];
  externalMessageId?: string;
  sentByOrgUserId?: number;
  broadcastId?: number;
  devotionalId?: number;
  prayerRequestId?: number;
  contextConfigId?: number;
  credits?: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
  user?: Users;
  broadcast?: Broadcasts;
  devotional?: Devotionals;
  prayerRequest?: PrayerRequests;
  contextConfig?: ContextConfigs;
  sentByOrgUser?: OrgUsers;
}

export enum OrgUserActivityType {
  CreateDevotional = 'CreateDevotional',
  CreateBroadcast = 'CreateBroadcast',
  CreateAction = 'CreateAction',
  CreateAutomation = 'CreateAutomation',
  CreateContextConfig = 'CreateContextConfig',
  UpdateDevotional = 'UpdateDevotional',
  UpdateBroadcast = 'UpdateBroadcast',
  UpdateAction = 'UpdateAction',
  UpdateAutomation = 'UpdateAutomation',
  UpdateContextConfig = 'UpdateContextConfig',
  RespondToPrayerRequest = 'RespondToPrayerRequest',
  SendMessageToUser = 'SendMessageToUser',
}

export type OrgUserActivityInfo = {};

export interface OrgUserActivities {
  id: string;
  orgId: number;
  orgUserId: number;
  type: OrgUserActivityType;
  info: OrgUserActivityInfo;
  broadcastId?: number;
  devotionalId?: number;
  prayerRequestId?: number;
  contextConfigId?: number;
  actionId?: number;
  credits?: number;
  auditVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  organization?: Organizations;
  user?: Users;
  broadcast?: Broadcasts;
  devotional?: Devotionals;
  prayerRequest?: PrayerRequests;
  contextConfig?: ContextConfigs;
  sentByOrgUser?: OrgUsers;
}

export type DevotionalTemplateConfig = {
  name: string;
  description?: string;
  keywords?: string[];
  devotionalType: DevotionalType;
  content: {
    subscribeMessage: string;
    subscribeMedia?: MessageMedia[];
    messages: {
      dayNum?: number;
      message: string;
      media?: MessageMedia[];
      time?: string;
      dateTime?: Date;
    }[];
  };
};

export type BroadcastTemplateConfig = {
  message: string;
  media?: MessageMedia[];
};

export type ActionTemplateConfig = {
  name: string;
  response: string;
  responseMedia?: MessageMedia[];
  keywords?: string[];
};

export type LinkTemplateConfig = {
  url: string;
};

export type ScholarshipTemplateConfig = {
  url: string;
  originalCost: string;
  newCost: string;
};

export type VideoTemplateConfig = {
  url: string; // This can be a streaming URL, like .m3u8 or an .mp4 file
  downloadUrl?: string; // We want the full url for download here, the .mp4 file
  canDownload?: boolean;
};

export type DownloadTemplateConfig = {
  url: string;
  contentType: string;
};

export type SurveyTemplateConfig = {
  content?: ContextConfigContent;
  repeat?: ContextConfigRepeat;
  status?: ContextConfigStatus;
  title?: string;
  type?: ContextConfigTypes;
};

export type TemplateConfig =
  | DevotionalTemplateConfig
  | BroadcastTemplateConfig
  | ActionTemplateConfig
  | LinkTemplateConfig
  | DownloadTemplateConfig
  | ScholarshipTemplateConfig
  | VideoTemplateConfig
  | SurveyTemplateConfig;

export type TemplateMetadata = {
  weeks?: number;
  messages?: number;
  creditsPerUser?: number;
  author?: string;
  topics?: string[];
};

// TODO: Deprecate PlaybookResource once Kits are updated
export type PlaybookResource = {
  id: string;
  type: PlaybookResourceType;
  title: string;
  description?: string;
  url: string;
};

// TODO: Deprecate PlaybookResourceType once Kits are updated
export enum PlaybookResourceType {
  LINK = 'link',
  FILE = 'file',
}

export type OrgKitTemplateCompletion = { [key: string]: boolean };

export type PaginationFilter = { page?: number; limit?: number };

export enum FilterMethod {
  greaterThanNumber = 'greaterThanNumber',
  lessThanNumber = 'lessThanNumber',
  greaterThanDate = 'greaterThanDate',
  lessThanDate = 'lessThanDate',
  equalTo = 'equalTo',
  notEqualTo = 'notEqualTo',
  contains = 'contains',
  oneOf = 'oneOf',
  betweenNumber = 'betweenNumber',
  betweenDate = 'betweenDate',
  oneOfArray = 'oneOfArray',
  notOneOfArray = 'notOneOfArray',
  notInArray = 'notInArray',
}

export interface AdvancedFilter {
  nestedKey?: string;
  field: string;
  method: FilterMethod;
  compareValue1: any;
  compareValue2?: any;
  isJson?: boolean;
}

export interface SearchQuery {
  orgId?: number;
  searchString?: string;
  entity: any;
  filters?: AdvancedFilter[];
  pagination?: PaginationFilter;
  useLimit?: boolean;
  permissionGroupScopingKey?: 'prayer' | 'stories' | 'people' | 'conversations' | 'broadcasts';
  customQueryOverride?: (query: any) => any; // This can only be used directly from the backend, not passed over the wire
  orderNulls?: 'NULLS FIRST' | 'NULLS LAST';
  searchFields?: string[];
  jsonSearchFields?: string[];
  orderBy?: string;
  orderDirection?: 'DESC' | 'ASC';
  withDeleted?: boolean;
}

export type PaginatedSearchQuery = Omit<SearchQuery, 'pagination'> & {
  pagination: Required<PaginationFilter>;
};

export type ProductInfo = {
  stripePriceId?: string;
  stripePriceCents?: number;
  stripeProductId?: string;
  stripeOveragePriceId?: string;
  stripeOneTimePriceId?: string;
  description?: string;
  legacyStripeProductId?: string;
  legacyStripePriceId?: string;
  legacyStripeMigration?: boolean;
  hideFromCart?: boolean;
  hideFromPlanCard?: boolean;
  additionalUnitOnePrice?: number;
  upgradeProductId?: string;
  bulletPoints?: string[];
  autoIncludeBarnaProduct?: boolean;
  hideFromManagePlanOptions?: boolean;
};

export type Invoices = {
  amount: number;
  createdAt: string;
  id: string;
  invoiceUrl: string;
  quantity: number;
};

export interface ProductCreateParams {
  name: string;
  /**
   * pricePerUnit should be in cents
   */
  pricePerUnit: number;
  type?: ProductType;
  unitQuantity: number;
  /**
   * required when pricePerUnit is starter | bundle
   */
  billingPeriodType?: BillingPeriodType;
}

export type ProductAddParams = {
  productId: string;
  stripePromoCodeId?: string;
  stripePaymentMethodId?: string;
};

export type ProductCancelParams = {
  productId: string;
};

export type DiscoverItem = HubDiscoverItem | KitDiscoverItem | TemplateDiscoverItem;

export const mapHubToDiscoverItem = (hub: Hubs): HubDiscoverItem => ({ type: DiscoverItemType.HUB, item: hub });
export const mapKitToDiscoverItem = (kit: Kits): KitDiscoverItem => ({ type: DiscoverItemType.KIT, item: kit });
export const mapTemplateToDiscoverItem = (template: Templates): TemplateDiscoverItem => ({
  type: DiscoverItemType.TEMPLATE,
  item: template,
});

export const mapFeatureableDiscoverItemToFeatureableDiscoverItemDescriptor = (
  featureableDiscoverItem: FeatureableDiscoverItem,
): FeatureableDiscoverItemDescriptor => {
  switch (featureableDiscoverItem.type) {
    case DiscoverItemType.HUB:
      return mapHubToHubDiscoverItemDescriptor(featureableDiscoverItem.item);
    case DiscoverItemType.KIT:
      return mapKitToKitDiscoverItemDescriptor(featureableDiscoverItem.item);
  }
};

export const mapHubToHubDiscoverItemDescriptor = (hub: Hubs): HubDiscoverItemDescriptor => ({
  type: DiscoverItemType.HUB,
  id: hub.id,
});

export const mapKitToKitDiscoverItemDescriptor = (kit: Kits): KitDiscoverItemDescriptor => ({
  type: DiscoverItemType.KIT,
  id: kit.id,
});

export type DiscoverItemDescriptor =
  | HubDiscoverItemDescriptor
  | KitDiscoverItemDescriptor
  | TemplateDiscoverItemDescriptor;

type TypedDiscoverItemDescriptor<T> = {
  type: T;
  id: string;
};

export type HubDiscoverItemDescriptor = TypedDiscoverItemDescriptor<DiscoverItemType.HUB>;
export type KitDiscoverItemDescriptor = TypedDiscoverItemDescriptor<DiscoverItemType.KIT>;
export type TemplateDiscoverItemDescriptor = TypedDiscoverItemDescriptor<DiscoverItemType.TEMPLATE>;

type TypedDiscoverItem<Discriminator, Item> = {
  type: Discriminator;
  item: Item;
};

export type HubDiscoverItem = TypedDiscoverItem<DiscoverItemType.HUB, Hubs>;
export type KitDiscoverItem = TypedDiscoverItem<DiscoverItemType.KIT, Kits>;
export type TemplateDiscoverItem = TypedDiscoverItem<DiscoverItemType.TEMPLATE, Templates>;

export type UpdateFeaturedDiscoverItemsRequestBody = { order: FeatureableDiscoverItemDescriptor[] };

export type FeatureableDiscoverItemDescriptor = HubDiscoverItemDescriptor | KitDiscoverItemDescriptor;

export type FeatureableDiscoverItem = HubDiscoverItem | KitDiscoverItem;
export type FavoriteableDiscoverItem = HubDiscoverItem | KitDiscoverItem;

export enum DiscoverItemType {
  HUB = 'hub',
  KIT = 'kit',
  TEMPLATE = 'template',
  SHOPIFY_PRODUCT = 'shopify_product',
  ASSESSMENT = 'assessment',
}

export type ShopifyProductMetadata = {
  // productId: string;
  variantId?: string;
  author?: string;
  topics?: string[];
};

export type DiscoverItemMetadata = TemplateMetadata & KitMetadata & HubMetadata & ShopifyProductMetadata;

export type DiscoverItemCard = {
  itemId: string;
  discoverType: DiscoverItemType;
  templateType?: TemplateType;
  devotionalType?: DevotionalType;
  scholarshipData?: ScholarshipTemplateConfig;
  videoData?: VideoTemplateConfig;
  metadata: DiscoverItemMetadata;
  title: string;
  shortDescription: string;
  thumbnailImageUrl?: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  orgTypeFiltering?: OrgTypeFiltering;
};

export type HubItemCard = DiscoverItemCard & { discoverType: DiscoverItemType.HUB };

export type ActionCreateRequestBody = {
  keywords?: string[];
  media?: MessageMedia[];
  name: string;
  response: string;
  orgKitId?: string;
  templateId?: string;
};

export type BroadcastCreateRequestBody = {
  time: string;
  message: string;
  media?: MessageMedia[];
  groups?: number[];
  users?: number[];
  primaryContactOrgIds?: number[];
  excludedGroups?: number[];
  excludedUsers?: number[];
  status?: BroadcastStatus;
  devotionalId?: number;
  orgKitId?: string;
  templateId?: string;
  messageFromName?: string;
  messageReplyToEmailAddress?: string;
  messageSubject?: string;
  richTextMessage?: string;
  type?: BroadcastType;
  isScheduled?: boolean;
  attachedAssessmentId?: string;
  nextSendDate?: string;
  broadcastInterval?: BroadcastInterval | null;
};

export type DevotionalCreateRequestBody = {
  keywords?: string[];
  name: string;
  description?: string;
  content?: {
    messages?: DevoMessage[];
    subscribeMessage?: string;
    subscribeMedia?: MessageMedia[];
  };
  status?: DevotionalStatus;
  type: DevotionalType;
  orgKitId?: string;
  templateId?: string;
};

export type OrgKitCreateRequestBody = {
  kitId: string;
  favorited: boolean;
  templateCompletion?: OrgKitTemplateCompletion;
};

export type OrgKitUpdateRequestBody = {
  favorited?: boolean;
  templateCompletion?: OrgKitTemplateCompletion;
};

export type OrgHubCreateRequestBody = {
  hubId: string;
  favorited: boolean;
};

export type OrgHubUpdateRequestBody = {
  favorited: boolean;
};

export type ReportMessagesSummaryResults = {
  inbound: number;
  outbound: number;
  total: number;
};

/**
 * All enum types in this file directly match to a Postgresql Type.
 * IMPORTANT: if you change any of the types then you need to update the same db type via migration
 */

export enum BillingPeriodType {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum ProductType {
  BUNDLE = 'bundle',
  STARTER = 'starter',
  ONE_TIME = 'one_time',
}

export enum DevotionalType {
  FIXED_DATE = 'fixed_date',
  RELATIVE_DATE = 'relative_date',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
}

export enum TemplateType {
  KEYWORD = 'keyword',
  BROADCAST = 'broadcast',
  SERIES = 'series',
  LINK = 'link',
  DOWNLOAD = 'download',
  SURVEY = 'survey',
  SCHOLARSHIP = 'scholarship',
  VIDEO = 'video',
  ASSESSMENT = 'assessment',
}

/**
 * End db types
 */

export enum AssessmentType {
  RCI_LONG = 'RCI_LONG',
  RCI_MEDIUM = 'RCI_MEDIUM',
  RCI_SHORT = 'RCI_SHORT',
  RSCI_LONG = 'RSCI_LONG',
  RSCI_MEDIUM = 'RSCI_MEDIUM',
  RSCI_SHORT = 'RSCI_SHORT',
  RCI_LONG_CREOLE = 'RCI_LONG_CREOLE',
  RCI_MEDIUM_CREOLE = 'RCI_MEDIUM_CREOLE',
  RCI_SHORT_CREOLE = 'RCI_SHORT_CREOLE',
  RCI_LONG_SPANISH = 'RCI_LONG_SPANISH',
  RCI_MEDIUM_SPANISH = 'RCI_MEDIUM_SPANISH',
  RCI_SHORT_SPANISH = 'RCI_SHORT_SPANISH',
}

export type RciOrRsciType = 'rci' | 'rsci';

export type RciCalculations = {
  type: AssessmentType;
  totalRCI: { score: number };
  personalCapital: {
    score: number;
    scores: {
      generalHealth: { score: number };
      mentalAndEmotionalWellbeing: { score: number };
      nutrition: { score: number };
      employment: { score: number };
      education: { score: number };
      financialWellbeing: { score: number };
      housingAndLivingSituation: { score: number };
      transportation: { score: number };
      clothing: { score: number };
    };
    components: {
      healthAndWellness: { includes: string[]; score: number };
      basicNeeds: { includes: string[]; score: number };
      knowledgeAndSkills: { includes: string[]; score: number };
    };
  };
  socialCapital: {
    score: number;
    scores: {
      familySupport: { score: number };
      significantOther: { score: number };
      socialSupport: { score: number };
      socialMobility: { score: number };
      healthyLifestyle: { score: number };
      accessToHealthcare: { score: number };
      safety: { score: number };
    };
    components: {
      familyAndHome: { includes: string[]; score: number };
      socialNetwork: { includes: string[]; score: number };
      healthyActivitiesAndEnvironment: { includes: string[]; score: number };
    };
  };
  culturalCapital: {
    score: number;
    scores: {
      beliefs: { score: number };
      values: { score: number };
      spirituality: { score: number };
      senseOfPurpose: { score: number };
      culturalRelevancy: { score: number };
      senseOfCommunity: { score: number };
    };
    components: {
      socialValues: { includes: string[]; score: number };
      spiritualityComponent: { includes: string[]; score: number };
      communityConnectedness: { includes: string[]; score: number };
    };
  };
};

export const RciDomainsKeys: { [key in keyof Omit<RciCalculations, 'type' | 'totalRCI'>]: string } = {
  personalCapital: 'Personal Capital',
  socialCapital: 'Social Capital',
  culturalCapital: 'Cultural Capital',
};

export const RciComponents: {
  personalCapital: { [key in keyof RciCalculations['personalCapital']['components']]: string };
  socialCapital: { [key in keyof RciCalculations['socialCapital']['components']]: string };
  culturalCapital: { [key in keyof RciCalculations['culturalCapital']['components']]: string };
} = {
  personalCapital: {
    healthAndWellness: 'Health and Wellness',
    basicNeeds: 'Basic Needs',
    knowledgeAndSkills: 'Knowledge and Skills',
  },
  socialCapital: {
    familyAndHome: 'Family and Home',
    socialNetwork: 'Social Network',
    healthyActivitiesAndEnvironment: 'Healthy Activities and Environment',
  },
  culturalCapital: {
    socialValues: 'Social Values',
    spiritualityComponent: 'Spirituality Component',
    communityConnectedness: 'Community Connectedness',
  },
};

export const RciIndicators: {
  personalCapital: { [key in keyof RciCalculations['personalCapital']['scores']]: string };
  socialCapital: { [key in keyof RciCalculations['socialCapital']['scores']]: string };
  culturalCapital: { [key in keyof RciCalculations['culturalCapital']['scores']]: string };
} = {
  personalCapital: {
    generalHealth: 'General Health',
    mentalAndEmotionalWellbeing: 'Mental and Emotional Wellbeing',
    nutrition: 'Nutrition',
    employment: 'Employment',
    education: 'Education',
    financialWellbeing: 'Financial Wellbeing',
    housingAndLivingSituation: 'Housing and Living Situation',
    transportation: 'Transportation',
    clothing: 'Clothing',
  },
  socialCapital: {
    familySupport: 'Family Support',
    significantOther: 'Significant Other',
    socialSupport: 'Social Support',
    socialMobility: 'Social Mobility',
    healthyLifestyle: 'Healthy Lifestyle',
    accessToHealthcare: 'Access to Healthcare',
    safety: 'Safety',
  },
  culturalCapital: {
    beliefs: 'Beliefs',
    values: 'Values',
    spirituality: 'Spirituality',
    senseOfPurpose: 'Sense of Purpose',
    culturalRelevancy: 'Cultural Relevancy',
    senseOfCommunity: 'Sense of Community',
  },
};

export interface ResponseMsg {
  code?: number;
  message: string;
}

export interface PayloadInfo {
  errors?: null | ResponseMsg[];
  warnings?: null | ResponseMsg[];
  info?: null | ResponseMsg[];
  meta?: null | {};
}

export interface ResponsePayload<TData = {}> extends PayloadInfo {
  data: null | TData;
}

export interface OnboardingData {
  plan: Plans;
  msgQuantity: number;
}

export interface OnboardingQueryParams {
  planKey?: string;
  plankey?: string;
  msgQuantity?: number;
  msgquantity?: number;
}

export interface VerifyNumberBody {
  code: string;
}

export type CreditsRewardConfig = {
  totalCredits: number;
};

export interface ShortenedUrls {
  id: string;
  longUrl: string;
  shortenedUrl: string;
  shortenedUrlSlug: string;
  orgId: number;
  orgUserId?: number;
  createdAt: Date;
}

export interface ShortenedUrlCreateParams {
  longUrl: string;
  orgId: number;
  orgUserId?: number;
}

export interface ShortenedUrlFindQuery {
  urls?: string[];
}

export interface ShortenedUrlMetadata {
  ipAddress: string | string[];
  refererDomain?: string;
  headers?: Record<string, unknown>;
}

export interface ShortenedUrlRequests {
  id: string;
  shortUrlId: string;
  createdAt: Date;
  metadata?: ShortenedUrlMetadata;
}

export interface ShortenedUrlRequestCreateParams {
  shortUrlId: string;
  info?: ShortenedUrlMetadata;
}

export interface ShortenedUrlsFindResponse {
  urls: ShortenedUrls[];
}

export type EventOccurrenceTask = {
  targetCount: number;
  matchKey?: string;
};

export type OneTimeThresholdTask = {
  threshold: number;
  matchKey?: string;
};

export type EventOccurrenceTaskProgress = {
  targetCount: number;
  currentCount: number;
};

/**
 * RewardCodes
 */

export interface RewardCodes {
  id: string; // uuid
  code: string; // unique
  rewardCodeType: RewardCodeType;
  rewards: Reward[];
  ownerOrgId?: number;
  ownerOrg?: Organizations;
  orgRewards?: OrgRewards[];
  rewardCodeUsage?: RewardCodeUsage;
  startDate?: Date;
  endDate?: Date;
  usageLimit?: number;
}

export enum RewardType {
  CREDITS = 'CREDITS',
  DISCOUNT = 'DISCOUNT',
  CUSTOM_PLAN = 'CUSTOM_PLAN',
  ACTIVATE_CHALLENGE = 'ACTIVATE_CHALLENGE',
}

export enum RewardCodeType {
  REFERRAL = 'REFERRAL',
  PROMO = 'PROMO',
  NETWORK = 'NETWORK',
}

export interface Reward {
  type: RewardType;
  config: RewardConfig;
}

export type RewardConfig = CreditsRewardConfig;

export type RewardCodeUsage = {
  paidCount?: number;
  freeCount?: number;
};

export type RewardCodeCreateParams = {
  rewardCodeType: RewardCodeType;
  rewards: Reward[];
  code?: string;
  ownerOrgId?: number;
  startDate?: Date;
  endDate?: Date;
  usageLimit?: number;
};

export type RewardCodeUpdateParams = RewardCodeCreateParams;

export interface RewardCodeValidationResult {
  isValid: boolean;
  rewardCodeType?: RewardCodeType;
  totalCredits?: number;
}

/**
 * OrgRewards
 */

export interface OrgRewards {
  id: string; // uuid
  rewardCodeId: string;
  rewardCode: RewardCodes;
  awardedOrgId: number;
  awardedOrg: Organizations;
  awardedAt?: Date;
}

export type OrgRewardCreateParams = {
  rewardCodeCode: string; // Code string to lookup the RewardCode by
  awardedAt?: Date;
};

export type OrgRewardUpdateParams = {
  rewardCodeCode?: string;
  awardedOrgId?: number;
  awardedAt?: Date;
};

export interface Explorers {
  id: string;
  text: string;
  status: ExplorerStatus;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  zipCode?: string;
  hubspotContactId?: string;
  routingType?: ExplorerRoutingType;
  manualRoutingReason?: ExplorerManualRoutingReason;
  info: ExplorerInfo;
  sourceMetadata?: ExplorerSourceMetadata | Record<string, unknown>;
  engagement: ExplorerEngagement;
  connectedTime?: Date;
  explorerCampaignId?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  currentConnection?: ExplorerConnections;
  explorerConnections?: ExplorerConnections[];
  explorerCampaign?: ExplorerCampaigns;
  urgent?: boolean;
  unflaggedUrgentAt?: Date;
  location?: any;
  disableAutoReRouting?: boolean;
  partnerId?: string;
  partner?: { id: string; name: string };
}

export enum ExplorerRoutingType {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC',
}

export enum ExplorerManualRoutingReason {
  AUTOMATIC_ROUTING_ERROR = 'AUTOMATIC_ROUTING_ERROR',
  INVALID_API_KEY = 'INVALID_API_KEY',
  INVALID_PERSON_PROFILE = 'INVALID_PERSON_PROFILE',
  POTENTIALLY_INAPPROPRIATE = 'POTENTIALLY_INAPPROPRIATE',
  NO_LOCAL_CHURCHES = 'NO_LOCAL_CHURCHES',
  NO_MESSAGE_TEXT = 'NO_MESSAGE_TEXT',
  NO_PHONE_NUMBER = 'NO_PHONE_NUMBER',
  INVALID_PHONE_NUMBER = 'INVALID_PHONE_NUMBER',
  NO_VALID_EXPLORER_LOCATION = 'NO_VALID_EXPLORER_LOCATION',
  MANUAL_ROUTING_REQUIRED = 'MANUAL_ROUTING_REQUIRED',
  ORGANIZATION_NOT_FOUND = 'ORGANIZATION_NOT_FOUND',
  URGENT = 'URGENT',
}

export enum ExplorerStatus {
  NEW = 'NEW',
  MATCHED = 'MATCHED',
  REJECTED = 'REJECTED',
}

export enum RejectionReasons {
  NOT_ENOUGH_INFO = 'NOT_ENOUGH_INFO',
  UNDER_13_YEARS_OLD = 'UNDER_13_YEARS_OLD',
  INVALID_ZIP = 'INVALID_ZIP',
  INAPPROPRIATE = 'INAPPROPRIATE',
  INTERNATIONAL = 'INTERNATIONAL',
  OTHER = 'OTHER',
}

export enum ExplorerSourceTopicKey {
  'PRAYER' = 'PRAYER',
  'LONELY' = 'LONELY',
  'ANXIETY' = 'ANXIETY',
  'SADNESS' = 'SADNESS',
  'RELATIONSHIPS' = 'RELATIONSHIPS',
  'MARRIAGE' = 'MARRIAGE',
  'NIGHT' = 'NIGHT',
  'FORGIVENESS' = 'FORGIVENESS',
  'LOSS' = 'LOSS',
  'STRESS' = 'STRESS',
  'BIBLE' = 'BIBLE',
  'ANGER' = 'ANGER',
  'BODY_IMAGE' = 'BODY_IMAGE',
  'MEANING' = 'MEANING',
  'HOPE' = 'HOPE',
  'BAD_THINGS' = 'BAD_THINGS',
  'CONNECT' = 'CONNECT',
}

export enum ExplorerSourceTopicName {
  'Prayer' = 'Prayer',
  'Lonely' = 'Lonely',
  'Anxiety' = 'Anxiety',
  'Sadness' = 'Sadness',
  'Relationships' = 'Relationships',
  'Marriage' = 'Marriage',
  'Night' = 'Night',
  'Forgiveness' = 'Forgiveness',
  'Loss' = 'Loss',
  'Stress' = 'Stress',
  'Bible' = 'Bible',
  'Anger' = 'Anger',
  'Body Image' = 'Body Image',
  'Meaning' = 'Meaning',
  'Hope' = 'Hope',
  'Bad Things' = 'Bad Things',
  'Connect' = 'Connect',
}

export const ExplorerSourceTopicKeyToNameMap: Record<ExplorerSourceTopicKey, ExplorerSourceTopicName> = {
  [ExplorerSourceTopicKey.PRAYER]: ExplorerSourceTopicName['Prayer'],
  [ExplorerSourceTopicKey.LONELY]: ExplorerSourceTopicName['Lonely'],
  [ExplorerSourceTopicKey.ANXIETY]: ExplorerSourceTopicName['Anxiety'],
  [ExplorerSourceTopicKey.SADNESS]: ExplorerSourceTopicName['Sadness'],
  [ExplorerSourceTopicKey.RELATIONSHIPS]: ExplorerSourceTopicName['Relationships'],
  [ExplorerSourceTopicKey.MARRIAGE]: ExplorerSourceTopicName['Marriage'],
  [ExplorerSourceTopicKey.NIGHT]: ExplorerSourceTopicName['Night'],
  [ExplorerSourceTopicKey.FORGIVENESS]: ExplorerSourceTopicName['Forgiveness'],
  [ExplorerSourceTopicKey.LOSS]: ExplorerSourceTopicName['Loss'],
  [ExplorerSourceTopicKey.STRESS]: ExplorerSourceTopicName['Stress'],
  [ExplorerSourceTopicKey.BIBLE]: ExplorerSourceTopicName['Bible'],
  [ExplorerSourceTopicKey.ANGER]: ExplorerSourceTopicName['Anger'],
  [ExplorerSourceTopicKey.BODY_IMAGE]: ExplorerSourceTopicName['Body Image'],
  [ExplorerSourceTopicKey.MEANING]: ExplorerSourceTopicName['Meaning'],
  [ExplorerSourceTopicKey.HOPE]: ExplorerSourceTopicName['Hope'],
  [ExplorerSourceTopicKey.BAD_THINGS]: ExplorerSourceTopicName['Bad Things'],
  [ExplorerSourceTopicKey.CONNECT]: ExplorerSourceTopicName['Connect'],
} as const;

export interface ExplorerSourceTopic {
  name: ExplorerSourceTopicName;

  key: ExplorerSourceTopicKey;
}

export const ExplorerSourceTopicList: ExplorerSourceTopic[] = Object.values(ExplorerSourceTopicKey).map(
  (key: ExplorerSourceTopicKey) => {
    return { key, name: ExplorerSourceTopicKeyToNameMap[key] };
  },
);

export type ExplorerInfo = {
  rejectionReasons?: RejectionReasons[];
};

export type ExplorerEngagement = {
  firstMessageSentTime?: string;
};

export interface ExplorerSettingsConfig {
  acceptsNationwide?: boolean;
  acceptsSpanishLanguage?: boolean;
  overrideMaxMatchDistanceMiles?: number;
  overrideMaxWeeklyConnections?: number;
  overrideMaxDailyConnections?: number;
  explorerTopicPreferences?: ReachTopicPreferences;
}

export interface GlooExplorerFormPayload {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  zipCode: string;
  text: string;
  disableAutoReRouting?: boolean;
  orgId?: number;
  campaignId?: string;
  sourceMetadata?: {
    topic?: string;
    language?: string;
    campaign?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    partnerSystemUid?: string;
    partnerSystemData?: string;
    sourceUrl?: string;
    pageId?: string;
  };
}

export interface GlooExplorerFormError {
  field: string;
  message: string;
}

export enum GlooExplorerFormResult {
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface ExplorerFormSubmissionsLogInterface {
  id: string;
  apiKey: string;
  partnerName: string;
  data: GlooExplorerFormPayload;
  result: GlooExplorerFormResult;
  errors?: GlooExplorerFormError[];
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;
}

export const EXPLORER_SETTINGS_INFINITE_WEEKLY_CONNECTIONS = 1000;
export const DEFAULT_MAX_MATCH_DISTANCE_MILES = 50;
export const DEFAULT_MAX_WEEKLY_CONNECTIONS = 14;
export const DEFAULT_MAX_DAILY_CONNECTIONS = 5;

export enum ExplorerLockReason {
  NOT_A_CHURCH = 'NOT_A_CHURCH',
  NOT_A_GOOD_FIT = 'NOT_A_GOOD_FIT',
  ABUSE = 'ABUSE',
  LACK_OF_PAYMENT = 'LACK_OF_PAYMENT',
  OTHER = 'OTHER',
}

export enum ExplorerSettingsPausedType {
  MANUAL = 'MANUAL',
  SYSTEM = 'SYSTEM',
  SCHEDULED = 'SCHEDULED',
  HARD = 'HARD',
}

export enum ExplorerSettingsPauseReason {
  NOT_A_GOOD_FIT = 'notAGoodFit',
  NUMBER_OF_EXPLORERS = 'numberOfExplorers',
  LACK_OF_CAPACITY = 'lackOfCapacity',
  EXPLORER_DISTANCE = 'explorerDistance',
  HOLIDAY = 'holiday',
  VACATION = 'vacation',
  WEEKEND = 'weekend',
  WRONG_TYPES_OF_CONNECTIONS = 'wrongTypesOfConnections',
  OTHER = 'other',
  ONBOARDING = 'onboarding',
  EXCEEDED_EXPLORER_DROP_ALLOWANCE = 'exceededExplorerDropAllowance',
}

export const displayExplorerSettingsPauseReason = (pauseReason: ExplorerSettingsPauseReason) => {
  switch (pauseReason) {
    case ExplorerSettingsPauseReason.NOT_A_GOOD_FIT:
      return 'Not a Good Fit';
    case ExplorerSettingsPauseReason.NUMBER_OF_EXPLORERS:
      return 'Number of Explorers';
    case ExplorerSettingsPauseReason.LACK_OF_CAPACITY:
      return 'Lack of Capacity';
    case ExplorerSettingsPauseReason.EXPLORER_DISTANCE:
      return 'Explorer Distance';
    case ExplorerSettingsPauseReason.HOLIDAY:
      return 'Holiday';
    case ExplorerSettingsPauseReason.VACATION:
      return 'Vacation';
    case ExplorerSettingsPauseReason.WEEKEND:
      return 'Weekend';
    case ExplorerSettingsPauseReason.WRONG_TYPES_OF_CONNECTIONS:
      return 'Wrong Types of Connections';
    case ExplorerSettingsPauseReason.OTHER:
      return 'Other';
    case ExplorerSettingsPauseReason.ONBOARDING:
      return 'Onboarding';
    case ExplorerSettingsPauseReason.EXCEEDED_EXPLORER_DROP_ALLOWANCE:
      return 'Exceeded Explorer Drop Allowance';
  }
};

export type ExplorerSettingsPauseInfo = {
  pauseType?: ExplorerSettingsPausedType;
  pausedAt?: Date;
  pauseDescription?: string;
  pausedByOrgUserId?: number;
};

export enum ExplorerSettingsBlockedReason {
  MISSING_EXPLORERS_PRODUCT = 'MISSING_EXPLORERS_PRODUCT',
  MISSING_ZIPCODE = 'MISSING_ZIPCODE',
  INVALID_LOCATION = 'INVALID_LOCATION',
  SUBSCRIPTION_NOT_ACTIVE = 'SUBSCRIPTION_NOT_ACTIVE',
  UNVERIFIED_ACCOUNT = 'UNVERIFIED_ACCOUNT',
}

export interface ExplorerSettings {
  id: string;
  orgId: number;
  pauseReason?: ExplorerSettingsPauseReason;
  pausedUntil?: Date;
  paused: boolean;
  pauseInfo: ExplorerSettingsPauseInfo;
  systemPausedAt: Date;
  systemPausedCount: number;
  createdAt: Date;
  deletedAt?: Date;
  updatedAt: Date;
  auditVersion: number;
  organization?: Organizations;
  config: ExplorerSettingsConfig;
  isLocked?: boolean;
  lockedAt?: Date;
  lockedByOrgUserId?: number;
  lockReason?: ExplorerLockReason;
  lockDescription?: string;
  excludeFromAutorouting: boolean;
}

export type ExplorerCampaignsInfo = {
  about: string;
  aboutFootnote?: string;
  benefits: string[];
  commitments: string[];
  covenant?: { name: string; link: string };
  link: string;
  position?: number;
};

export enum ExplorerCampaignsTag {
  CURRENTLY_RUNNING = 'Currently Running',
  LAUNCHING_SOON = 'Launching Soon',
}

export interface ExplorerMatchingConfig {
  disableEmailsToExplorers?: boolean;
  explorerMatchedEmailTemplateId?: string | null;
  outboundWebhookURL?: string | null;
}

export interface Partners {
  id: string;
  name: string;
  code: string;
  explorerMatching: ExplorerMatchingConfig;
  explorers?: Explorers[];
  explorerCampaigns?: ExplorerCampaigns[];
  deletedAt?: Date;
  apiKey?: string;
  tollFreeNumber?: string | null;
}

export interface ExplorerCampaigns {
  id: string;
  title: string;
  description?: string | null;
  logoUrl?: string | null;
  imageUrl?: string | null; // The the default image to show for reach campaigns
  desktopDetailsPageCoverImageUrl?: string | null;
  mobileCardCoverImageUrl?: string | null;
  mobileDetailsPageCoverImageUrl?: string | null;
  tag?: ExplorerCampaignsTag | null;
  info: ExplorerCampaignsInfo | null;
  partnerId?: string;
  partner?: Partners;
  createdAt: Date;
  deletedAt?: Date;
  updatedAt: Date;
  auditVersion: number;
  showInUI?: boolean;
  organizationIds?: number[];
  shouldRouteToApprovedOrgsOnly?: boolean;
  siteSlug?: string;
  siteUrl?: string;
  siteKey?: string;
  orgKey?: string;
}

export interface ExplorerConnections extends AuditableEntity {
  id: string;
  explorerId: string;
  orgId: number;
  status: ExplorerConnectionStatus;
  explorerMessagingUserId?: number;
  assignedToOrgUserIds?: number[];
  matchedAt?: Date;
  engagedAt?: Date;
  connectedAt?: Date;
  disconnectedAt?: Date;
  rematchedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  messageSentToExplorerAt?: Date;
  messageReceivedFromExplorerAt?: Date;
  firstViewedAt?: Date;
  orgUserAssignedDate?: Date;
  auditVersion: number;
  explorerMessagingUser?: Users;
  assignedToOrgUsers?: OrgUsers[];
  organization?: Organizations;
  explorer?: Explorers;
  surveySent?: Date;
  restrictedOrgUserIds?: RestrictedExplorerUserIds[];
}

export interface RestrictedExplorerUserIds {
  orgUserId: number;
  explorerConnectionId: string; // uuid
}

export enum ExplorerConnectionStatus {
  MATCHED = 'MATCHED',
  ENGAGED = 'ENGAGED',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RE_MATCHED = 'RE_MATCHED',
  COMPLETED = 'COMPLETED',
}

export interface ExplorerSourceMetadata {
  topic?: string;
  language?: 'en' | 'es';
  partnerName?: string;
  partnerApiKey?: string;
  partnerSystemData?: string;
  partnerSystemUid?: string;
  partnerSystemUserData?: string;
  campaign?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  passthrough?: string;
  sourceUrl?: string;
}

export interface ExplorerSimulatorMessageGrading {
  trust: {
    score: number;
    reason: string;
  };
  empathy: {
    score: number;
    reason: string;
  };
  curiosity: {
    score: number;
    reason: string;
  };
}
export interface ExplorerSimulatorMessageGradingResponse {
  data: ExplorerSimulatorMessageGrading;
}

export interface AddressAutocompleteFields {
  label: string;
  value: AddressAutocompleteResponse;
}

export interface AddressAutocompleteResponse {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  string?: string;
}

export type PlanSelectedProducts = { product: Products; quantity?: number };
export type PlanSelectedProductsIdOnly = { productId: string; quantity?: number };

export enum MinistryAreas {
  CHURCH = 'CHURCH',
  TEACHING = 'TEACHING',
  OUTREACH = 'OUTREACH',
  SCHOOLS = 'SCHOOLS',
  OTHER = 'OTHER',
}

export enum MinistryKeys {
  ADULT_SUNDAY_SCHOOL = 'ADULT_SUNDAY_SCHOOL',
  ART_PROGRAMS = 'ART_PROGRAMS',
  BIBLE_CLASSES = 'BIBLE_CLASSES',
  CHILDRENS_MINISTRY = 'CHILDRENS_MINISTRY',
  CHURCH_CHOIR = 'CHURCH_CHOIR',
  COUNSELING = 'COUNSELING',
  CROSS_CULTURAL_MINISTRY = 'CROSS_CULTURAL_MINISTRY',
  DEAF_MINISTRY = 'DEAF_MINISTRY',
  ELEMENTARY_SCHOOL = 'ELEMENTARY_SCHOOL',
  FOOD_PANTRY = 'FOOD_PANTRY',
  GLOBAL_MISSIONS = 'GLOBAL_MISSIONS',
  HOMELESS_MINISTRY = 'HOMELESS_MINISTRY',
  HOMESCHOOL = 'HOMESCHOOL',
  LOCAL_COMMUNITY_OUTREACH = 'LOCAL_COMMUNITY_OUTREACH',
  MARRIAGE_ENRICHMENT = 'MARRIAGE_ENRICHMENT',
  MARRIAGE_PREPARATION = 'MARRIAGE_PREPARATION',
  MENS_MINISTRY = 'MENS_MINISTRY',
  MUSIC_DRAMA_PRODUCTIONS = 'MUSIC_DRAMA_PRODUCTIONS',
  NURSERY = 'NURSERY',
  PRESCHOOL = 'PRESCHOOL',
  PRISON_MINISTRY = 'PRISON_MINISTRY',
  RECOVERY_GROUPS = 'RECOVERY_GROUPS',
  SENIORS_MINISTRY = 'SENIORS_MINISTRY',
  SMALL_GROUPS = 'SMALL_GROUPS',
  WOMENS_MINISTRY = 'WOMENS_MINISTRY',
  YOUNG_ADULTS_COLLEGE_MINISTRY = 'YOUNG_ADULTS_COLLEGE_MINISTRY',
  YOUTH_PROGRAMS = 'YOUTH_PROGRAMS',
}

export const Ministries: { [key in MinistryKeys]: string } = {
  [MinistryKeys.ADULT_SUNDAY_SCHOOL]: 'Adult Sunday School',
  [MinistryKeys.ART_PROGRAMS]: 'Art Programs',
  [MinistryKeys.BIBLE_CLASSES]: 'Bible Classes',
  [MinistryKeys.CHILDRENS_MINISTRY]: 'Childrens Ministry',
  [MinistryKeys.CHURCH_CHOIR]: 'Church Choir',
  [MinistryKeys.COUNSELING]: 'Counseling',
  [MinistryKeys.CROSS_CULTURAL_MINISTRY]: 'Cross Cultural Ministry',
  [MinistryKeys.DEAF_MINISTRY]: 'Deaf Ministry',
  [MinistryKeys.ELEMENTARY_SCHOOL]: 'Elementary School',
  [MinistryKeys.FOOD_PANTRY]: 'Food Pantry',
  [MinistryKeys.GLOBAL_MISSIONS]: 'Global Missions',
  [MinistryKeys.HOMELESS_MINISTRY]: 'Homeless Ministry',
  [MinistryKeys.HOMESCHOOL]: 'Homeschool',
  [MinistryKeys.LOCAL_COMMUNITY_OUTREACH]: 'Local Community Outreach',
  [MinistryKeys.MARRIAGE_ENRICHMENT]: 'Marriage Enrichment',
  [MinistryKeys.MARRIAGE_PREPARATION]: 'Marriage Preparation',
  [MinistryKeys.MENS_MINISTRY]: 'Mens Ministry',
  [MinistryKeys.MUSIC_DRAMA_PRODUCTIONS]: 'Music Drama Productions',
  [MinistryKeys.NURSERY]: 'Nursery',
  [MinistryKeys.PRESCHOOL]: 'Preschool',
  [MinistryKeys.PRISON_MINISTRY]: 'Prison Ministry',
  [MinistryKeys.RECOVERY_GROUPS]: 'Recovery Groups',
  [MinistryKeys.SENIORS_MINISTRY]: 'Seniors Ministry',
  [MinistryKeys.SMALL_GROUPS]: 'Small Groups',
  [MinistryKeys.WOMENS_MINISTRY]: 'Womens Ministry',
  [MinistryKeys.YOUNG_ADULTS_COLLEGE_MINISTRY]: 'Young Adults College Ministry',
  [MinistryKeys.YOUTH_PROGRAMS]: 'Youth Programs',
};

export const MinistryAreasMinistries: { [key in MinistryAreas]: Array<MinistryKeys> } = {
  [MinistryAreas.CHURCH]: [
    MinistryKeys.ADULT_SUNDAY_SCHOOL,
    MinistryKeys.CHILDRENS_MINISTRY,
    MinistryKeys.MENS_MINISTRY,
    MinistryKeys.SENIORS_MINISTRY,
    MinistryKeys.WOMENS_MINISTRY,
    MinistryKeys.YOUNG_ADULTS_COLLEGE_MINISTRY,
    MinistryKeys.YOUTH_PROGRAMS,
  ],
  [MinistryAreas.TEACHING]: [
    MinistryKeys.ART_PROGRAMS,
    MinistryKeys.BIBLE_CLASSES,
    MinistryKeys.CHURCH_CHOIR,
    MinistryKeys.MARRIAGE_ENRICHMENT,
    MinistryKeys.MARRIAGE_PREPARATION,
    MinistryKeys.MUSIC_DRAMA_PRODUCTIONS,
    MinistryKeys.RECOVERY_GROUPS,
    MinistryKeys.SMALL_GROUPS,
  ],
  [MinistryAreas.OUTREACH]: [
    MinistryKeys.COUNSELING,
    MinistryKeys.CROSS_CULTURAL_MINISTRY,
    MinistryKeys.DEAF_MINISTRY,
    MinistryKeys.FOOD_PANTRY,
    MinistryKeys.GLOBAL_MISSIONS,
    MinistryKeys.HOMELESS_MINISTRY,
    MinistryKeys.LOCAL_COMMUNITY_OUTREACH,
    MinistryKeys.PRISON_MINISTRY,
  ],
  [MinistryAreas.SCHOOLS]: [
    MinistryKeys.ELEMENTARY_SCHOOL,
    MinistryKeys.HOMESCHOOL,
    MinistryKeys.NURSERY,
    MinistryKeys.PRESCHOOL,
  ],
  [MinistryAreas.OTHER]: [],
};

export const MinistriesList = Object.keys(Ministries).sort() as MinistryKeys[];

export enum Demographics {
  EMPLOYMENT_STATUS = 'employmentStatus',
  GENDER = 'gender',
  ORIENTATION = 'orientation',
  MARITAL_STATUS = 'maritalStatus',
  HIGHEST_EDUCATION = 'highestEd',
  INCOME = 'income',
  SUBSTANCE_GOAL = 'substanceGoal',
  SUBSTANCE_USE = 'substanceUse',
  IDENTITY_TERMS = 'identityTerms',
}

export const DemographicKeys: { [key in Demographics]: string } = {
  [Demographics.EMPLOYMENT_STATUS]: 'Employment Status',
  [Demographics.GENDER]: 'Gender',
  [Demographics.ORIENTATION]: 'Orientation',
  [Demographics.MARITAL_STATUS]: 'Marital Status',
  [Demographics.HIGHEST_EDUCATION]: 'Highest Education',
  [Demographics.INCOME]: 'Income',
  [Demographics.SUBSTANCE_GOAL]: 'Substance Goal',
  [Demographics.SUBSTANCE_USE]: 'Substance Use',
  [Demographics.IDENTITY_TERMS]: 'Identity Terms',
};

export type NetworkReferralReportOrg = {
  activeUsers: number | null;
  explorersReceived: number;
  explorerStatus: 'active' | 'paused' | 'locked' | null;
  orgId: number;
  orgName: string;
  city?: string;
  state?: string;
  primaryContact?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  explorerTopic: string;
  reasonForInactivity: ExplorerInactivityReason;
  lengthOfInactivity: string | null;
};

export type ExplorerInactivityReason = ExplorerSettingsPauseReason | ExplorerLockReason | null;

export type NetworkReferralReport = { organizations: NetworkReferralReportOrg[] };

export interface SubscriberMetricsOverTime {
  startTimestamp: string;
  subscribers: number;
}

export interface UnsubscriberMetricsOverTime {
  startTimestamp: string;
  unsubscribers: number;
}

export interface SubscriberAndUnsubscriberCount {
  allTimeSubscriberCount: number;
  subscribers: SubscriberMetricsOverTime[];
  thirtyDaySubscriberCount: number;
  unsubscribers: UnsubscriberMetricsOverTime[];
}

export type MessageMetricResult = {
  day?: string;
  userId?: string;
  totalInbound: number;
  totalOutbound: number;
  users?: Map<string, MessageMetricResult>;
  hourly?: MessageMetricResult[];
};

export type MetaMessageMetric = {
  totalInbound: number;
  totalOutbound: number;
  totalBroadcasts?: number;
  daily: Map<string, MessageMetricResult>;
  validations?: {
    total: number;
  };
};

export type ExplorerStatusMetrics = {
  matchedCount: number;
  connectedCount: number;
  completedCount: number;
  engagedCount: number;
  disconnectedCount: number;
  allTimeCount?: number;
};

export type ExplorerStatusOverTimeMetrics = ExplorerStatusMetrics & {
  startTimestamp: string;
};

export type SubscriberMetrics = {
  unsubscribers: number;
  subscribers: number;
};

export interface SubscriberMetricsOverTime {
  startTimestamp: string;
  subscribers: number;
}

export interface UnsubscriberMetricsOverTime {
  startTimestamp: string;
  unsubscribers: number;
}

export interface SubscriberAndUnsubscriberCount {
  allTimeSubscriberCount: number;
  thirtyDaySubscriberCount: number;
  unsubscribers: UnsubscriberMetricsOverTime[];
  subscribers: SubscriberMetricsOverTime[];
}

export type KeywordMetric = {
  keyword: string;
  keywordId: number;
  keywordUsesCount: number;
};

export type SeriesMetric = {
  seriesName: string;
  seriesId: number;
  seriesSubscriberCount: number;
};

export type FeatureInteractionsMetric = {
  allTimeTotalCount: number;
  thirtyDayIncomingPrayerRequests: number;
  thirtyDayKeywordInteractions: number;
  thirtyDaySeriesSubscribers: number;
  thirtyDayTotalCount: number;
  allTimeUserCount: number;
  thirtyDayUserCount: number;
};

export type MessageLogMetric = {
  timestamp: string;
  userId: string;
  orgId?: string;
  direction?: string;
};

export type ResponseTimeByInterval = {
  startTimestamp: string;
  responseTimeInMinutes: number | null;
};

export interface ResponseTimes {
  averageResponseTime: number | null;
  responseTimesByInterval: ResponseTimeByInterval[];
}

export type SmsCounts = {
  totalInbound: number;
  totalOutbound: number;
};

export type DailySmsMetric = {
  intervalStart: string;
  users: (SmsCounts & { userId: string })[];
  hourly: (SmsCounts & { intervalStart: string })[];
};

export type MetricOverTime = {
  startTimestamp: Date;
  count: number;
};

export type FeatureInteractionsOverTimeMetrics = {
  prayerRequestsOverTime: MetricOverTime[];
  keywordRequestsOverTime: MetricOverTime[];
  inboundOtherRequestsOverTime: MetricOverTime[];
  outboundRequestsOverTime: MetricOverTime[];
};

export type SmsMetricsOutput = {
  daily: DailySmsMetric[];
  incomingMessageTotals: {
    total: number;
    explorerTotal: number;
    userCount: number;
  };
  outgoingMessageTotals: {
    orgUserTotals: { [key: string]: number };
    orgUserTotal: number;
    total: number;
  };
  responseTime: {
    teamAverage: number;
    orgUserAverage: { [key: string]: number };
  };
  heatmap: SmsDailyHeatmap[];
  validations?: {
    total: number;
  };
};

export type Day = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export type DayRange = 'Morning' | 'Afternoon' | 'Evening';

export type SmsDailyHeatmap = {
  id: DayRange;
  data: {
    x: Day;
    y: number;
  }[];
};

export type AnalyticEvent = {
  name: string;
  params: object;
  timestamp: Date;
};

export type ConversationPromptStatus = 'Published' | 'Unpublished' | 'Archived' | 'Draft';

export interface ConversationPrompts {
  id: string;
  name: string;
  status: ConversationPromptStatus;
  message: string;
  messageMedia?: MessageMedia[];
  promptTip?: string;
  videoUrl?: string;
  author: string;
  authorImageUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  createdByOrgUserId?: number;
  updatedByOrgUserId?: number;
  topics: number[];
  topicsArr?: ReferenceLists[];
  createdByOrgUser?: OrgUsers;
  updatedByOrgUser?: OrgUsers;
}

export interface SuggestedTopics {
  id: number;
  value: string;
  title: string;
  frequency: number;
}

export interface ConversationPromptQuery {
  searchString?: string;
  filters?: AdvancedFilter[];
  pagination?: PaginationFilter;
  searchFields?: string[];
  showArchived?: boolean;
  showVideos?: boolean;
}

export interface ConversationPromptCombinedParams {
  topicIds?: number[];
  authors?: string[];
  itemsPerType?: number;
  type?: ConversationPromptsV2ItemTypes;
}

export type ConversationPromptCombinedData = {
  discoverVideos: ConversationPromptsV2NormalizedData[];
  conversationPrompts: ConversationPromptsV2NormalizedData[];
  responseTips: ConversationPromptsV2NormalizedData[];
  followUpQuestions: ConversationPromptsV2NormalizedData[];
  videoResponseTips: ConversationPromptsV2NormalizedData[];
};

export interface ConversationPromptsV2NormalizedData {
  id: string;
  topics?: number[];
  author?: string | null;
  authorImageUrl?: string;
  title?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
  type?: string;
  originalItem: ConversationPrompts & ReferenceLists;
}

export enum ConversationPromptsV2ItemTypes {
  CONVERSATION_PROMPTS = 'CONVERSATION_PROMPTS',
  DISCOVER_VIDEOS = 'DISCOVER_VIDEOS',
  FOLLOW_UP_QUESTIONS = 'FOLLOW_UP_QUESTIONS',
  RESPONSE_TIPS = 'RESPONSE_TIPS',
}

export type ConversationPromptsV2ResponseTipFilters = 'inbox' | 'drawer';

export type OrganizationActivityContexts =
  | ExplorerSettingsUnpausedContext
  | ExplorerSettingsPausedContext
  | ExplorerMatchedContext
  | ExplorerSettingsLockedContext
  | ExplorerRematchedContext;

export type OrganizationActivityDomains =
  | ExplorerSettingsUnpaused
  | ExplorerSettingsPaused
  | ExplorerMatched
  | ExplorerSettingsLocked
  | ExplorerRematched;

export interface OrganizationActivities {
  id: string;
  orgId: number;
  activityAt: Date;
  activityType: OrganizationActivityTypes;
  activityContext: OrganizationActivityContexts;
  actorOrgUserId: number;
  createdAt: Date;
  updatedAt: Date;
}

export type OrganizationActivitiesPayload = OrganizationActivityDomains & {
  orgId: number;
  activityAt: Date;
  actorOrgUserId?: number;
};

export enum OrganizationActivityTypes {
  EXPLORER_SETTINGS_PAUSED = 'EXPLORER_SETTINGS_PAUSED',
  EXPLORER_SETTINGS_UNPAUSED = 'EXPLORER_SETTINGS_UNPAUSED',
  EXPLORER_SETTINGS_LOCKED = 'EXPLORER_SETTINGS_LOCKED',
  EXPLORER_MATCHED = 'EXPLORER_MATCHED',
  EXPLORER_REMATCHED = 'EXPLORER_REMATCHED',
}

export type ExplorerSettingsUnpaused = {
  activityType: OrganizationActivityTypes.EXPLORER_SETTINGS_UNPAUSED;
  activityContext: ExplorerSettingsUnpausedContext;
};

export type ExplorerSettingsUnpausedContext = {
  automated: boolean;
  actorName?: string;
};

export type ExplorerSettingsPausedContext = {
  automated: boolean;
  pauseDescription?: string;
  pauseType: ExplorerSettingsPausedType;
  pausedUntil: Date;
  pauseReason?: string;
};

export type ExplorerSettingsPaused = {
  activityType: OrganizationActivityTypes.EXPLORER_SETTINGS_PAUSED;
  activityContext: ExplorerSettingsPausedContext;
};

export type ExplorerSettingsLocked = {
  activityType: OrganizationActivityTypes.EXPLORER_SETTINGS_LOCKED;
  activityContext: ExplorerSettingsLockedContext;
};

export type ExplorerSettingsLockedContext = {
  lockReason?: string;
  actorName?: string;
};

export type ExplorerMatched = {
  activityType: OrganizationActivityTypes.EXPLORER_MATCHED;
  activityContext: ExplorerMatchedContext;
};

export type ExplorerMatchedContext = {
  explorerId: string;
  explorerName?: string;
  explorerTopic?: string;
  automated: boolean;
  actorName?: string;
  urgent: boolean;
};

export type ExplorerRematchedContext = {
  explorerId: string;
  explorerName?: string;
  explorerTopic?: string;
  automated: boolean;
  actorName?: string;
  urgent: boolean;
};
export type ExplorerRematched = {
  activityType: OrganizationActivityTypes.EXPLORER_REMATCHED;
  activityContext: ExplorerRematchedContext;
};

export type FailingNumber = {
  number: string;
  failureCount: number;
  organizationName?: string;
  contactEmail?: string;
  errorCode: number;
  errorDescription: string;
  failedAt: Date;
};

export type MessageFailureCreateParams = {
  messageId: string;
  errorCode: string;
  description: string;
  sentFrom: string;
  sentTo: string;
  sentFromOrgId?: number;
  sentToUserId?: number;
  provider: 'bandwidth';
  direction: 'in' | 'out';
  sentAt: Date;
  failedAt: Date;
};

export enum SuggestedResponderPersonas {
  PASTOR = 'pastor',
  THERAPIST = 'therapist',
}

export interface SuggestedResponsesQuery {
  message: string;
  targetUser?: Users;
  responderPersona?: SuggestedResponderPersonas;
  suggestionCount?: number;
  maxTokens?: number;
  temperature?: number;
  model?: string;
}

export type ScheduledPauses = {
  id: string;
  startDate: string;
  endDate: string;
  orgId: number;
  pauseReason?: ExplorerSettingsPauseReason;
  pauseDescription?: string;
  createdByOrgUserId?: number;
  organization?: Organizations;
  createdByOrgUser?: OrgUsers;
};

export type LoggedOutCookie = 'yes' | 'no' | undefined;

export type CurrentUser = {
  uid: string;
  displayName?: string;
  email: string;
  avatar?: string;
  emailVerified: boolean;
};

export interface CurrentAccountDecorator {
  isRecovery: boolean;
  hasAccessToExplorers: boolean;
  isFreeTrial: boolean;
  isExpiredSubscription: boolean;
  isActive: boolean;
  isUnverified: boolean;
  hasSMSProduct: boolean;
  organizationBrand: {
    type?: OrgType;
    brand?: OrgBrand;
  };
  activeSMSSubscription?: SubscriptionProducts | null;
  paidSMSSubscription?: SubscriptionProducts | null;
  creditCount: number;
  subscriptionStatus?: SubscriptionStatus;
  isPaymentInvalid: boolean;
  additionalProductsCost: number;
  subscriptionCost: number;
  isAuthenticVerified: boolean;
  isAuthenticRejected: boolean;
  isAwaitingAuthenticityCheck: boolean;
  canDoMMS: boolean;
  networkRewardCode?: RewardCodes;
}

export type NewPermissionsCheck = { key: OrgUserPermissionKeys; allAccess?: boolean };

export interface CurrentUserDecorator {
  isSuperAdmin: boolean;
  isOrganizationAdmin: boolean;
  currentPermissions?: OrgUserPermissions;
  initials: string;
}

export interface Profile {
  user: (OrgUsers & CurrentUserDecorator) | null;
  currentAccount: (Organizations & CurrentAccountDecorator) | null;
  accounts: Pick<Organizations, 'id' | 'name' | 'createdAt' | 'status' | 'config'>[] | null;
}

export type OrgUserPermissionAllViewNone = 'all' | 'view' | 'none';
export type OrgUserPermissionAllNone = 'all' | 'none';
export type OrgUserPermissionsNumberOrNull = number[] | null;
export type OrgUserPermissionKeys = keyof Omit<
  OrgUserPermissions,
  'orgId' | 'orgUserId' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'auditVersion' | 'organization' | 'orgUser'
>;
export interface OrgUserPermissions {
  orgId: number;
  orgUserId: number;
  admin: boolean; // default to true
  // null = no permissions, [] = all people, [groupId1, groupId2] = limited
  prayer?: number[] | null;
  stories?: number[] | null;
  people?: number[] | null;
  conversations?: number[] | null;
  broadcasts?: number[] | null;
  keywords?: OrgUserPermissionAllViewNone;
  series?: OrgUserPermissionAllViewNone;
  surveys?: OrgUserPermissionAllViewNone;
  measure?: OrgUserPermissionAllNone;
  explorers?: OrgUserPermissionAllNone;
  reachSettings?: OrgUserPermissionAllNone;
  engageSettings?: OrgUserPermissionAllNone;
  adminSettings?: OrgUserPermissionAllNone;
  glooPlusPurchaser?: OrgUserPermissionAllNone;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  organization?: Organizations;
  orgUser?: OrgUsers;
}

export interface OrganizationInvitations {
  id: string;
  accountName: string;
  accountType: OrganizationTypes;
  zipCode: string;
  status: 'pending' | 'claimed' | 'deleted';
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  invitedBy?: string;
  website?: string;
  networkCode?: string;
  invitations?: Partial<Invitations>[];
  orgId?: number;
  claimedDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
  organization?: Organizations;
}

// These bandwidth statuses come from https://dev.bandwidth.com/apis/numbers/#tag/Toll-Free-Verification/operation/getVerificationStatus
export type BandwidthVerificationStatus =
  | 'VERIFIED'
  | 'UNVERIFIED'
  | 'PENDING'
  | 'PARTIALLY_VERIFIED'
  | 'INVALID_STATUS'
  | 'Verification - Requested'
  | 'Verification Requested'
  | 'Waiting For Customer'
  | 'Waiting for Support'
  | 'In Progress'
  | 'Resolved - Verified'
  | 'Resolved - Not Verified';

export type BandwidthVerificationEndUserStatuses =
  | 'pending_submission'
  | 'verified'
  | 'unverified'
  | 'error'
  | 'submitted';

export interface BandwidthVerificationRequests {
  id: string;
  phone: string; // Format +1888#######
  status: BandwidthVerificationEndUserStatuses;
  bandwidthStatus?: BandwidthVerificationStatus;
  latestMessage?: string;
  submittedDate?: Date;
  verifiedDate?: Date;
  lastBandwidthCheckDate?: Date;
  orgId: number;
  organization?: Organizations;
}

export type BandwidthCheckStatusResponse = {
  declineReasonDescription?: string;
  phoneNumber: string;
  resubmitAllowed?: boolean;
  status: any;
  ticketNumber?: string;
  verificationRequestId?: string;
};

export type TeamMemberInvitation = {
  id?: number;
  firstName: string;
  lastName?: string;
  email: string;
  orgUserPermissions: Partial<OrgUserPermissions>;
};

export enum ScheduleVariant {
  Immediate = 'immediate',
  Scheduled = 'scheduled',
}

export enum UserImportStatus {
  UPLOADED = 'UPLOADED',
  PROCESS_WITHOUT_CHECKS = 'PROCESS_WITHOUT_CHECKS',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  PENDING_ON_ACCOUNT_AGE = 'PENDING_ON_ACCOUNT_AGE',
  FAILED_ON_PRECHECK_ORGANIZATION_EXISTS = 'FAILED_ON_PRECHECK_ORGANIZATION_EXISTS',
  FAILED_ON_PRECHECK_ORGANIZATION_AUTHENTICITY = 'FAILED_ON_PRECHECK_ORGANIZATION_AUTHENTICITY',
  FAILED_ON_PRECHECK_AWA = 'FAILED_ON_PRECHECK_AWA',
  FAILED_ON_PRECHECK_INCREMENTAL_NUMBERS = 'FAILED_ON_PRECHECK_INCREMENTAL_NUMBERS',
  FAILED_ON_PRECHECK_EXCESS_DUPLICATE_NAMES = 'FAILED_ON_PRECHECK_EXCESS_DUPLICATE_NAMES',
  FAILED_ON_IMPORT = 'FAILED_ON_IMPORT',
}

export type UserImportResult = {
  success: boolean;
  count: number;
  existingUsers?: { id: number; first: string; last: string; phone: string }[];
  updatedUserIds?: number[];
};

export type UserImport = {
  id: string;
  status: UserImportStatus;
  results?: UserImportResult;
  orgId: number;
  orgUserId: number;
  filename: string;
  organization?: Organizations;
  orgUser?: OrgUsers;
  createdAt: Date;
};

export type UserImportUpsertParams = {
  filename: string;
};

export type UserImportRow = {
  firstName: string;
  lastName?: string;
  email?: string;
  address?: string;
  phone?: string;
  smsNumber?: string;
  groupIds?: number[];
};

// needed in gloo-ui and across the apps
// TODO: Had to comment this out to fix the backend build since "JSX" is not available
// export type SelectOption<Value = any> = { label: string | JSX.Element; value: Value };
export type SelectOption<Value = any> = { label: string | any; value: Value };

export type MatchRequestsMatchType = 'NEW' | 'RE_MATCH';

/*
 * EXPLORER: the explorer themself selected to rematch via Gloo Form Widget
 * ORG: the rematch was triggered by an Org Admin
 * SYSTEM: the periodic background job
 */
export type MatchRequestsInitiatorType = 'EXPLORER' | 'ORG' | 'SYSTEM';

export interface MatchRequests {
  id: string;

  matchServiceRequestId?: string;
  explorerId?: string;
  partnerId?: string;
  explorerCampaignId?: string;
  resolvedOrganizationId?: number;

  text: string;
  status: ExplorerStatus;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
  info: ExplorerInfo;
  engagement: ExplorerEngagement;
  connectedTime?: Date;
  urgent: boolean;
  location?: any;
  routingType?: ExplorerRoutingType;
  manualRoutingReason?: ExplorerManualRoutingReason;
  formSubmittedAt?: Date;
  sourceMetadata?: ExplorerSourceMetadata | Record<string, unknown>;
  disableAutoReRouting: boolean;
  unflaggedUrgentAt?: Date;
  rawMatchServicePayload: unknown;

  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;

  matchType: MatchRequestsMatchType;
  rejectionMessage?: string;
  matchInitiator?: MatchRequestsInitiatorType;
}

export interface ExplorerConnectionMatchRequests {
  id: string;

  explorerConnectionId: string;
  matchRequestId: string;

  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  auditVersion: number;
}

export type SurveyStatus = 'PENDING' | 'SENT' | 'SENDING' | 'ERROR' | 'UNSUBSCRIBED';
export interface OrgResponderSurvey {
  id: string;
  explorerId: string;
  orgId: number;
  status: SurveyStatus;
  surveyResponse: { rating?: number; additionalFeedback?: string };
  phoneNumber: string;
  errorCount?: number;
}

export type UsersBulkUpdateData = Pick<Users, 'id'> & Partial<Users>;

export interface WidgetConfig {
  debug: boolean;
  apiEndpointUrl: string;
  apiBaseUrl: string;
  embedQuery: string;
  apiKey: string;
  campaign: string;
  campaignId: string;
  /** TODO: create new ReachTopicPreferences type */
  topic: string;
  disableMessageTransmission?: boolean;
  disableMessage?: boolean; // alias for disableMessageTransmission, for backwards compatibility.
  language: WidgetConfigLanguage;
  sourcePartnerSystemUid: string;
  sourcePartnerSystemData: string;
  pageId?: string;
  redirectOnSuccessUrl?: string;
  disableAutoReRouting?: boolean;
  orgId?: number;
  disableEmail?: boolean;
  postalCodeRequired?: boolean;
  disablePostalCode?: boolean;

  text: {
    formName: string;
    primaryHeadline: string;
    primarySubheadline: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    phonePlaceholder: string;
    postalCodePlaceholder: string;
    emailPlaceholder: string;
    messagePlaceholder: string;
    postalCodeCaption: string;
    submitButton: string;
    defaultFormErrorMessage: string;
    successMessageHeadline: string;
    successMessageSubheadline: string;
    successMessageIconEmoji: string;
    messageDisabledDefault: string;
    termsOfService?: string;
    alreadyConnected?: string;
    alreadyConnectedHeadline?: string;
    alreadyConnectedButtonConfirm?: string;
    alreadyConnectedButtonCancel?: string;
  };
}

export type WidgetConfigLanguage = 'en' | 'es';

export type SessionEntity = {
  sessionToken: string;
  userId: number;
  expires: Date;
  user: OrgUsers;
};

export enum FeatureActivationFeatureKeys {
  EXPLORERS = 'EXPLORERS',
}
